/**
 * verse/index.ts — VERSE Orchestration
 *
 * Vowel-Echo Response Synthesis Engine.
 *
 * This is the entry point. It receives a GeneratorContext (player text +
 * cortex state), runs the full pipeline, and returns a single string —
 * the Aeterna's voice.
 *
 * Pipeline:
 *   0. Pre-Router          — classify register; intercept greetings, meta,
 *                            affective symbols, and compound phrases BEFORE
 *                            kernel extraction can mangle them
 *   1. Kernel Extraction   — find the semantic anchor in the player's words
 *   2. Discourse Threading — check for cross-turn echoes
 *   3. Planning            — select operators from cortex state
 *   4. Transformation      — apply each operator to produce phrase segments
 *   5. Synthesis           — assemble, apply prosody, validate
 *   6. Discourse Record    — push the response's kernel for future turns
 */

import { extractKernel, detectRelationalContext, extractProperNounsFromText, type KernelResult, type ClassifyContext } from "./kernel.js";
import { OPERATORS, type TransformContext, type OperatorType } from "./transform.js";
import { planResponse, resetPlannerState, type PlanContext } from "./planner.js";
import { synthesize, type SynthesisSegment } from "./synthesizer.js";
import { pushKernel, findEcho, pushPlayerText, getConversationArc, processEntities, getEntity, getEntities, resolvePronouns, getLastMentionedEntity, type DiscourseEcho } from "./discourse.js";
import * as PC from "./whispers/pre-compose.js";
import type { ProsodyParams } from "./prosody.js";
import type { CortexState } from "../neural/cortex.js";

// ─── Content-Word Dedup Skip List ───────────────────────────────────────────
// Common sentence starters / pronouns that should NOT be registered as
// core content words — prevents "I hear that." from poisoning "I see you."
const DEDUP_SKIP = new Set([
  "i", "that", "something", "what", "there", "yeah", "right",
  "here", "it", "this", "say", "more",
]);

// ─── Structural Hollow Detection (module-level constants) ──────────────────
const STRUCTURAL_ARTICLES = new Set(["the", "that", "a", "an", "this", "your", "my"]);
const DEGENERATE_PHRASES = new Set([
  "nothing to add", "nothing more needed", "nothing else to say",
  "still to add", "still present", "it's present",
]);

// ─── Structural Hollow Detection ──────────────────────────────────────────────
// Detects operator output that is grammatically valid but communicatively empty:
//   "The knocking." — article + single noun, says nothing
//   "That scratch." — demonstrative + single noun
//   "The life is." — dangling copula without predicate
//   "Knocking." — bare single word
// These appear when VERSE picks a low-value kernel and ECHO/NOMINALIZE
// can't make anything meaningful from it. The guardrail triggers fallback
// to INTERROGATE or ECHO_TOGETHER, which produce engagement instead.
function isStructurallyHollow(text: string): boolean {
  const trimmed = text.replace(/[.…?!]+$/, "").trim().toLowerCase();
  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;

  // Single word: "Knocking." / "Life." — always hollow as a response
  if (words.length === 1) return true;

  // "The X" / "That X" / "A X" / "This X" — article + single content word
  if (words.length === 2) {
    if (STRUCTURAL_ARTICLES.has(words[0])) return true;
    // "X is" — dangling copula
    if (words[1] === "is" || words[1] === "was") return true;
  }

  // "The X is" / "That X is" — article + noun + dangling copula
  if (words.length === 3) {
    if (STRUCTURAL_ARTICLES.has(words[0]) && (words[2] === "is" || words[2] === "was")) return true;
  }

  // "Something about the X" — vague frame with no content
  if (words.length <= 5 && /^something (?:about|in|like) (?:the |that |a |an )?/i.test(trimmed)) {
    // But "something about the way she looked" is OK (6+ words with content)
    if (words.length <= 4) return true;
  }

  // Degenerate operator outputs: phrases that carry zero communicative value.
  if (DEGENERATE_PHRASES.has(trimmed)) return true;

  return false;
}

// ─── Public Interface ────────────────────────────────────────────────────────

export interface GeneratorContext {
  /** Raw player text (exactly as typed) */
  playerText: string;
  /** Full cortex state from the neural pipeline */
  cortex: CortexState;
  /** Last N Aeterna responses for anti-repetition checking */
  recentResponses: string[];
  /** Liquid personality state */
  personality: { patience: number; curiosity: number; warmth: number; wit: number };
  /** Room mood dimensions (0-1) */
  roomMood?: { warmth: number; energy: number; mystery: number; nature: number };
  /** In-game hour (0-23) */
  gameHour?: number;
  /** Season string */
  season?: string;
  /** MeMo recalls that were surfaced this turn */
  memoRecalls?: Array<{ summary: string; layer: string }>;
}

// ─── Seeded PRNG ─────────────────────────────────────────────────────────────

function mulberry32(seed: number): () => number {
  return function () {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 0xffffffff;
  };
}

function hashText(s: string): number {
  let h = 0x9e3779b9;
  for (let i = 0; i < s.length; i++) {
    h = Math.imul(h ^ s.charCodeAt(i), 0x85ebca6b);
    h ^= h >>> 13;
  }
  return (h >>> 0) || 1;
}

// ─── Holding Responses (quality safety net) ──────────────────────────────────

function pick<T>(arr: T[], rand: () => number): T {
  return arr[Math.floor(rand() * arr.length)];
}

type Strategy = "containment" | "activation" | "grounding" | "expansion";

function holdingResponse(strategy: Strategy, rand: () => number, recents?: Set<string>, text?: string): string {
  return PC.composeHolding(strategy, text ?? "", rand, recents);
}

// ─── Conversational Act Detection ───────────────────────────────────────────
// What is the player DOING with their words? Not register (how they're speaking)
// but act (what social move they're making). A bid is a bid at any intensity.
// Closure is closure whether whispered or emphatic. These bypass VERSE entirely
// because kernel extraction would mangle the intent into a single word and lose
// everything that matters about the move.

type ConversationalAct =
  | "bid"              // "Want to hear something?" — offering to share
  | "closure"          // "Thank you for being here" — wrapping up, expressing gratitude
  | "reassurance"      // "Is that normal?" — seeking validation
  | "testing"          // "Do you actually care?" — probing the relationship
  | "story_opening"    // "So this weird thing happened" — about to narrate
  | "self_diminish"    // "Sorry this is a lot" — minimizing self, apologizing for existing
  | "permission"       // "Can I ask you something?" — checking if the door is open
  | null;              // Not a conversational act — proceed to register classification

// ── Bid Detection: player offering to share something ─────────────────────
const RE_BID = /\b(want to hear|wanna hear|guess what|you know what('s| is)?|can I tell you|let me tell you|get this|you'll never (guess|believe)|I have (something|a thing|news)|ready for this|here's the thing|okay so get this|listen to this|you're not gonna believe)\b/i;

// BID_RESPONSES removed — now generative via PC.composeBid()

// ── Closure/Gratitude: player wrapping up or thanking ─────────────────────
const RE_CLOSURE = /\b(thank(s| you)( (for|so much))?|this (helped|means|meant|was (good|nice|helpful|what I needed))|I (needed|appreciate) (this|that)|that('s| is) (enough|all I needed|what I needed|exactly what)|good talk|I feel better( now)?|this was (nice|good|real|helpful)|this meant (a lot|something|everything|so much))\b/i;

// CLOSURE_RESPONSES removed — now generative via PC.composeClosure()

// ── Reassurance-Seeking: player wanting validation ────────────────────────
const RE_REASSURANCE = /\b(is that (normal|okay|weird|crazy|bad|wrong|stupid|dumb)|am I (crazy|weird|wrong|stupid|being|overreacting|making|the only)|does that (make sense|sound|seem)|is (it|this) (okay|normal|weird)|should I (be|feel|worry)|do (other )?people (feel|think|do)|I'm not (crazy|weird|wrong|alone|the only)( (am|right|for))?\??)\b/i;

// REASSURANCE_RESPONSES removed — now generative via PC.composeReassurance()

// ── Testing/Probing: player testing the relationship ──────────────────────
const RE_TESTING = /\b(do you (actually|really|even|genuinely) (care|listen|understand|mean|feel|hear|remember)|are you (just saying that|pretending|faking|performing|even real|actually listening)|you don't (really|actually|even) (care|listen|understand|get it|know)|you're (just|not even|probably) (saying|pretending|faking|a program|a bot|an ai)|is (this|any of this|anything you say) (real|genuine|honest|true)|how do I know (you|this is|you're)|prove it|mean that\??|say that to everyone)\b/i;

// TESTING_RESPONSES removed — now generative via PC.composeTesting()

// ── Story Opening: player about to narrate ────────────────────────────────
const RE_STORY_OPENING = /^(so\s+(this|the|today|yesterday|last|one|my)|okay so|alright so|oh my god so|omg so|dude so|bro so|man so|you ('re not gonna|won't|wouldn't) believe|the (weirdest|craziest|funniest|strangest|wildest|most) thing|something (happened|weird|crazy|funny|wild)|I (gotta|have to|need to) tell you|so (basically|essentially|long story)|wait (till|until|let me tell) you)\b/i;
// Also: mid-sentence story flag for compound messages
const RE_STORY_FLAG = /\b(you('re| are) not gonna believe|you won't believe what)\b/i;

// STORY_OPENING_RESPONSES removed — now generative via PC.composeStoryOpening()

// ── Self-Diminishment: player apologizing for existing ────────────────────
const RE_SELF_DIMINISH = /\b(sorry (this (is|got|was|feels?)|I'm being|to (dump|vent|bother|unload|burden|trouble|complain)|for (being|talking|saying|dumping|venting|complaining|rambling|bothering|bugging|this))|I'm (being (so )?)?(dramatic|annoying|stupid|dumb|pathetic|needy|clingy|too much|a lot|a mess|ridiculous|silly)|I'm so (dramatic|annoying|stupid|dumb|pathetic|needy|clingy|ridiculous|silly)|this is (probably )?(stupid|dumb|silly|ridiculous|nothing|not important|not a big deal|petty|trivial)|I know (this is|it's|I'm being|I shouldn't|it doesn't|nobody cares)|it('s| is) (not a big deal|not important|nothing|probably stupid|probably dumb|not that serious|whatever)|ignore me|never\s?mind|forget (it|I said|about it)|I shouldn't (have said|be|have brought|bother)|this (got|is getting) (heavy|dark|deep|intense|too much|a lot))\b/i;

// SELF_DIMINISH_RESPONSES removed — now generative via PC.composeSelfDiminish()

// ── Permission-Seeking: player checking if the door is open ───────────────
const RE_PERMISSION = /(?:^|(?:hey|hi|hello|so|okay|ok|um|uh|well)\s*,?\s*)(can I (ask|say|tell|share|talk (about|to you about)|bring up|mention|be (?:honest|real|straight|frank|upfront|candid))|is it (okay|ok|alright|fine) (if|to)|would it be (okay|ok|weird|alright) (if|to)|do you (mind if|want to hear|have (time|a (minute|moment|sec))))\b/i;
// Vulnerability-adjacent permission: these are heavier, but the act is the same — checking if the door is open
const RE_VULNERABILITY_OPENER = /\b(I need to (tell|say)|haven't told anyone|never told|I want to (tell|say|share)|there's something I|I've been (?:meaning|wanting|trying|waiting) to (?:tell|ask|say|share|bring up)|don't judge me|between you and me|just between us|promise (?:me )?(?:you won't|not to))\b/i;

// PERMISSION_RESPONSES removed — now generative via PC.composePermission()

// ── Act Classifier ────────────────────────────────────────────────────────

export function detectConversationalAct(text: string): ConversationalAct {
  const stripped = text.trim();
  const wordCount = stripped.split(/\s+/).filter(Boolean).length;

  // Permission-seeking: short, typically opens a conversation
  // Must check BEFORE bid, because "Can I tell you something?" is permission, not bid
  if (wordCount <= 12 && RE_PERMISSION.test(stripped)) return "permission";
  // Vulnerability-adjacent openers: heavier, but same act — checking if it's safe to share
  if (wordCount <= 12 && RE_VULNERABILITY_OPENER.test(stripped)) return "permission";

  // Self-diminishment: can appear at any point, any length
  if (RE_SELF_DIMINISH.test(stripped)) {
    // Guard: if others are ATTRIBUTING the diminishment to the player,
    // that's social pressure, not self-diminishment.
    // "My family thinks I'm being dramatic" → social pressure, NOT self_diminish
    const othersAttributing = /\b(?:everyone|everybody|people|they|my (?:friends?|family|parents?|mom|dad|brother|sister|partner|wife|husband|boss|coworkers?)) (?:says?|thinks?|told me|keeps? (?:telling|saying)|calls? me|called me)\b/i.test(stripped);
    if (!othersAttributing) {
      // But only if the message is SHORT or STARTS with the diminishment.
      // "I know it's not a big deal but my dad died" should NOT be classified as self_diminish —
      // the diminishment is a frame, but the content is grief. Only classify if the
      // diminishment IS the message (short) or dominates it.
      if (wordCount <= 8) return "self_diminish";
      // Check if the diminishment is in the first clause (before any "but"/"though")
      const firstClause = stripped.split(/\b(but|though|however|anyway|still)\b/i)[0];
      if (firstClause.length < stripped.length * 0.5) {
        // Diminishment is a short prefix before real content — don't intercept
      } else {
        return "self_diminish";
      }
    }
  }

  // Closure/Gratitude: typically short, wrapping up
  if (RE_CLOSURE.test(stripped) && wordCount <= 15) return "closure";

  // Testing/Probing: can be any length, but the test is the point
  if (RE_TESTING.test(stripped)) return "testing";

  // Reassurance-seeking: typically a question, any length
  if (RE_REASSURANCE.test(stripped)) return "reassurance";

  // Story opening: typically starts the message — check BEFORE bid because
  // "Okay so you're not gonna believe this" is a story opening, not just a bid.
  // Story openings have ^ anchors (they start the message) which gives them specificity.
  // BUT: if the message is 15+ words, the player already told the story — it's a full
  // share, not an opening. "so like, my coworker just got fired..." is the story itself.
  if (wordCount <= 14 && (RE_STORY_OPENING.test(stripped) || RE_STORY_FLAG.test(stripped))) return "story_opening";

  // Bid: offering to share (checked after story_opening)
  if (RE_BID.test(stripped) && wordCount <= 15) return "bid";

  return null;
}

// ─── Pre-Router: Register Classifier ────────────────────────────────────────

type MessageRegister = "high_affect" | "meta" | "casual" | "affective_symbol";

// ── Response Pools ──────────────────────────────────────────────────────────

// IDENTITY_RESPONSES removed — now generative via PC.composeIdentity()

// AETERNA_OPINION_RESPONSES removed — now generative via PC.composeOpinion()

// FACTUAL_RESPONSES removed — now generative via PC.composeFactual()

// WARMTH_RESPONSES removed — now generative via PC.composeWarmth()

// GREETING_RESPONSES removed — now generative via PC.composeGreeting()

// ── Classification Patterns ─────────────────────────────────────────────────

// META: Identity questions
const RE_IDENTITY = [
  /^(who|what) are you/i,
  /tell me about yourself/i,
  /are you (real|alive|conscious|sentient|a bot|an? ai|an? computer|here|there|listening)/i,
  /what do you (think|feel|want|know|believe)\s*\??$/i,
  /how do you (feel|think)/i,
  /what are you/i,
  /what (?:can|do) you (?:see|notice|perceive|sense|observe|detect|pick up|read)/i,
  /what (?:data|information) do you (?:see|have|get|receive|access|process)/i,
  /do you (?:actually |really )?(?:think|feel|want|know|believe|care|dream|remember|miss anyone)\??$/i,
];

// META: Understanding / connection questions — player checking if they're heard
const RE_UNDERSTANDING = [
  /do you understand me/i,
  /do you (get|hear|see) me/i,
  /are you (even )?listening/i,
  /do you know what I mean/i,
];

// UNDERSTANDING_RESPONSES removed — now generative via PC.composeUnderstanding()

// META: Existential / "why are you?" questions
const RE_EXISTENTIAL = [
  /^why are you\??$/i,
  /^why do you exist/i,
  /why were you (made|created|built)/i,
];

// EXISTENTIAL_RESPONSES removed — now generative via PC.composeExistential()

// META: Self-awareness / opinion questions
const RE_OPINION = [
  /do you have (feelings|opinions|thoughts|a soul|consciousness)/i,
  /can you (feel|think|love|care|dream)/i,
  /are you (happy|sad|lonely|bored|tired)/i,
];

// META: Factual questions the Aeterna can't answer
const RE_FACTUAL = [
  /what (time|day|date|year) is it/i,
  /what('s| is) the (time|date|weather)/i,
  /how old are you/i,
  /where are you/i,
  /what's your name/i,
  /what is your name/i,
];

// AFFECTIVE_SYMBOL: Pure emoji / hearts
const RE_EMOJI_ONLY = /^[\s<3♥❤️💕😊🥰🤍💛💙💚🖤😘🥺💗🫶✨💖💜🤗😍]+$/;
const RE_HEARTS = /^<3+$/;

// AFFECTIVE_SYMBOL: Extended laughter — "hahahahaha" / "HAHAHA" / "hehehehe"
const RE_PURE_LAUGHTER = /^(?:ha){2,}h?$|^(?:he){2,}h?$|^(?:lol)+$|^lmao$|^lmfao$|^rofl$/i;
// LAUGHTER_RESPONSES removed — now generative via PC.composeLaughter()

// AFFECTIVE_SYMBOL: Direct love declarations and relational affirmations
const RE_DIRECT_LOVE = [
  /^i love you\b/i,
  /^ily\b/i,
  /^ilysm\b/i,
  /^love you\b/i,
  /^luv u\b/i,
  /^i adore you\b/i,
  /\bI (?:really )?(?:care about|miss|need|trust|appreciate) you\b/i,
  /\byou(?:'re| are) (?:important|special|everything|the world|all I|my world|my everything)\b/i,
  /\byou (?:matter|mean (?:a lot|everything|the world|so much)) to me\b/i,
  /\bI miss you\b/i,
  /\bthank you for (?:being here|staying|listening|being|everything)\b/i,
];

// CASUAL: Greetings
const RE_GREETING = /^(hi|hey|hello|howdy|yo|sup|good\s+(morning|afternoon|evening|night)|g'day|what's up|wassup|heya|hiya|henlo|mornin[g']?)\b/i;

// CASUAL: "How are you" variants — aeterna-directed questions about its own state
const RE_HOW_ARE_YOU = /^how (are you|are things|is it going|you doing|do you do|are you feeling|do you feel)\b/i;
// Direct questions about the Aeterna's inner state
const RE_AETERNA_QUESTION = /^(?:what do you (?:think|feel|know) (?:about|of) me|do you (?:have feelings|care|understand|like me|love me|know me)|are you (?:real|alive|sentient|conscious)|what are you|who are you|why do you exist)\b/i;

// ── Compound Affect Phrases ─────────────────────────────────────────────────
// Multi-word phrases whose affect the CharCNN may misread.

type CompoundResult = { compose: "greeting" | "amusedExpletive" } | { holding: Strategy } | null;

// Laughter / amusement markers that soften expletives
const RE_AMUSEMENT = /\b(lol|lmao|lmfao|haha|hahaha|hehe|rofl|😂|🤣|😆)\b/i;

// AMUSED_EXPLETIVE_RESPONSES removed — now generative via PC.composeAmusedExpletive()

function detectCompoundAffect(text: string): CompoundResult {
  // Expletive-enthusiasm (high positive, not anger)
  if (/\b(fuck|hell|shit)\s*yeah\b/i.test(text)) return { compose: "greeting" };
  if (/\byeah\s*(baby|buddy)\b/i.test(text)) return { compose: "greeting" };

  // Agreement/acknowledgment with profanity → casual positive
  if (/\b(fuck(en|in[g']?)?\s*(ok(ay)?|right|sure|fine|alright|yep|yes))\b/i.test(text)) return { compose: "greeting" };
  if (/\b(ok(ay)?|right|sure|fine|alright)\s*fuck(en|in[g']?)?\b/i.test(text)) return { compose: "greeting" };

  // Frustration expletives — but check for amusement modifiers first.
  // "what the fuck lol" is amused disbelief, not crisis.
  // "what the fuck" alone IS crisis/frustration.
  if (/\b(what the fuck|what the hell|wtf|fml|fuck(ing)?\s+what|holy\s+(shit|fuck))\b/i.test(text)) {
    if (RE_AMUSEMENT.test(text)) return { compose: "amusedExpletive" };
    return { holding: "containment" };
  }

  return null;
}

// ── Meta-Commentary Detection ───────────────────────────────────────────────
// Player stepped outside the fiction to comment on the system itself.
// NOTE: "feels off/weird/wrong" only matches with system-referencing subject (this/that/it/your response)
// to avoid matching "something feels off" or "everything feels off" (personal emotional statements).
const RE_META_COMMENTARY = /\b(testing|test your|not quite there|not quite what I|not what I (meant|said|asked)|feel better|more (accurate|dynamic|natural|real|intelligent)|your response|you sound|(?:this|that|it|the response|your (?:response|answer|reply)) feels? (off|weird|wrong|robotic|scripted|natural|unnatural)|is this working|way of doing|could be better|needs work|gotta feel|needs? (much )?(deeper|more|better|improvement)|not good enough|not (quite )?right|do better|improve|deeper levels?|more depth|(?:need|needs) (?:to be |)(?:expanded|improved|fixed|updated|changed|reworked|overhauled|rebuilt)|(?:another|new|different|better) (?:intelligence |)(?:layer|approach|system|method|engine)|respond(?:ed)? (?:more |)(?:better|properly|naturally|normally)|how can we fix you|fix (?:this|that|you|yourself))\b/i;

// Expanded: direct complaints about the system's behavior
const RE_META_COMPLAINT = /\bwhy (can'?t|don'?t|won'?t|aren'?t) you\b|\byou('re| are) (not |just |always |never )?(respond|answer|listen|understand|help|work|making sense)\b|(?:basic|simple|normal|real|natural) convers|\bthat('s| is) not (what|how) (i|I)\b|you just said|you (keep|always|never) (saying|repeating|responding)|why (do|did) you (say|respond)|can you (just |please |actually )?(respond|answer)|(?:that |this |it )(?:made|makes|doesn'?t make) (?:no )?sense|(?:this is|that was|this still|that's|it's) (?:a |so |really |way |super |totally |completely |just )?(?:fucked|broken|busted|garbage|terrible|awful|useless|bad|wrong|off|horrible|trash|crappy|lame)(?:\b|\s+(?:response|answer|reply|output|thing))|wtf (?:was|is) that|what the (?:hell|fuck) (?:was|is) that|(?:none|nothing) of (?:that|this) (?:made|makes) sense|(?:that|this|it) (?:doesn'?t|didn'?t|does not|did not) make (?:any )?sense/i;

// META_COMMENTARY_RESPONSES removed — now generative via PC.composeMetaCommentary()

// META_COMPLAINT_RESPONSES removed — now generative via PC.composeMetaComplaint()

// ── Directed Frustration: player telling the Aeterna IT is being rude/bad ───
// Not meta-complaint (about the system) but interpersonal frustration (about the relationship).
const RE_DIRECTED_FRUSTRATION = /\b(?:you(?:'re| are) (?:being )?(?:so |really |actually |incredibly |super )?(?:rude|mean|hurtful|cruel|dismissive|cold|condescending|patronizing|insensitive|unhelpful|useless|annoying|weird|strange|off|a (?:jerk|dick|ass|asshole|bitch))|damn you|fuck you|screw you|you(?:'re| are) (?:so |really |actually )?(?:rude|mean|hurtful|disrespectful|awful|terrible|unhelpful|useless|weird|strange)(?: (?:with|to) me)?|(?:that was|that's|that is) (?:so |really )?(?:rude|mean|hurtful|cruel|dismissive|cold|harsh|disrespectful|fucked up|unhelpful|weird)|you hurt (?:me|my feelings)|how dare you|who do you think you are|don't (?:talk|speak) to me (?:like|that)|you (?:don't|do not) (?:care|understand|get it|listen|try|give a)|stop (?:being|acting) (?:so )?(?:weird|strange|cold|rude|mean|dismissive|annoying|creepy|distant))\b/i;

// DIRECTED_FRUSTRATION_RESPONSES removed — now generative via PC.composeDirectedFrustration()

// ── Possessive Correction: player correcting perceived overstep ──────────
// "MY baby girl, not yours!" / "That's MY thing" / "She's MINE"
// CRITICAL: Must require ADVERSARIAL context — "not yours", ALL-CAPS MY/MINE,
// or "I said/meant my". Plain "she is my X" is info sharing, not correction.
// The "not yours" / "not your" pattern is the strongest signal.
const RE_POSSESSIVE_CORRECTION_CI = /\b(?:not yours?\b|not your\b|(?:I said|I meant) (?:mine|my ))\b/i;
// Case-sensitive: standalone emphatic MY or MINE (the emphatic caps signal correction)
const RE_POSSESSIVE_CAPS = /\bM(?:Y|INE)\b/;

// POSSESSIVE_CORRECTION_RESPONSES removed — now generative via PC.composePossessiveCorrection()

// ── Dismissive Disengagement: player checking out in frustration ─────────
// "Fucken okay then" / "whatever then" / "fine then" / "I'm done"
const RE_DISMISSIVE_DISENGAGE = /\b(?:fuck(?:en|in[g']?)?\s+(?:ok(?:ay)?|fine|whatever|alright|sure|right|great)|(?:ok(?:ay)?|fine|whatever|alright)\s+then|I'm\s+(?:done|out|leaving|over (?:this|it))|forget\s+(?:it|this)|nvm|nevermind|w\/e|(?:yeah|yep|sure)\s+(?:whatever|fine)|screw\s+(?:this|it)|done\s+(?:with this|talking))\b/i;

// DISMISSIVE_DISENGAGE_RESPONSES removed — now generative via PC.composeDismissiveDisengage()

// ── Event Sharing: player sharing something that HAPPENED ───────────────
// "My girlfriend came to visit" / "I got the job" / "We had a great time"
// The player is NARRATING, sharing news. VERSE would mangle this into word-games.
// Detect the EVENT, assess its VALENCE, respond to what HAPPENED.

const RE_POSITIVE_EVENT = /\b(?:(?:came|coming) (?:to (?:visit|see|stay)|over|back|home)|got (?:the (?:job|promotion|offer|part|role)|accepted|in(?:to)?|engaged|married|hired|promoted)|passed (?:my|the|her|his) (?:exam|test|interview|audition)|(?:said|says) yes|(?:asked|proposed|offered)|we(?:'re| are) (?:having|expecting|pregnant|getting married|moving)|(?:finally|just) (?:happened|worked out|got|finished|made it|did it)|had (?:a (?:great|amazing|wonderful|incredible|awesome|fantastic|perfect|beautiful|lovely|good) (?:time|day|night|weekend|date|trip|vacation|experience))|won (?:the|a|my)|graduated|celebrated|surprised me|moved in|started (?:dating|seeing|a new)|found (?:a|the|my) (?:place|apartment|house|job)|she(?:'s| is) (?:pregnant|here|coming|visiting|staying)|he(?:'s| is) (?:here|coming|visiting|staying|back))\b/i;

const RE_NEGATIVE_EVENT = /\b(?:(?:lost|lose) (?:my|the|her|his) (?:job|home|house|apartment|cat|dog|pet|baby|mom|dad|parent|grandma|grandpa|friend)|died|passed away|broke up|(?:got|been|was) (?:fired|dumped|cheated on|arrested|diagnosed|evicted|rejected|laid off|hit|hurt|attacked|robbed)|had (?:a (?:terrible|awful|horrible|bad|rough|hard|miserable) (?:time|day|night|week|experience))|(?:she|he|they) left (?:me|us)|accident|hospitalized|surgery|cancer|funeral|miscarriage|overdose|relapsed)\b/i;

const RE_EVENT_SHARING = /\b(?:my (?:girlfriend|boyfriend|partner|wife|husband|friend|mom|dad|brother|sister|son|daughter|cat|dog|pet|boss|teacher|coworker)\s+(?:came|went|got|said|did|made|found|lost|bought|moved|started|finished|called|texted|told|asked|gave|took|left|arrived|visited|surprised|proposed|graduated))\b/i;

// Positive exclamation: "Today was amazing!" / "It was so good!" / "Best day ever!"
// No event verb needed — the exclamation itself signals positive sharing.
const RE_POSITIVE_EXCLAMATION = /\b(?:(?:today|yesterday|it|that|this|the (?:day|night|weekend|trip|date|time)) (?:was|is) (?:so |really |incredibly |absolutely |truly )?(?:amazing|incredible|awesome|wonderful|fantastic|perfect|beautiful|great|lovely|magical|spectacular|breathtaking|life-changing|unbelievable)|best (?:day|night|time|weekend|trip|experience|thing) (?:ever|of my life|I've ever)|I'm so (?:happy|excited|grateful|lucky|blessed|thrilled|stoked)|(?:so |really |incredibly )?(?:happy|excited|grateful|lucky|blessed|thrilled|stoked) (?:right now|today|about))\b/i;

// ── Event Extraction: pull the subject + action from the player's words ───
// "My girlfriend came to visit me" → subject: "your girlfriend", action: "came to visit"
// "I got the job" → subject: null, action: "got the job"
// "We had an amazing time" → subject: null, action: "had an amazing time"

interface ExtractedEvent {
  subject: string | null;   // "your girlfriend", "your mom" (null = player is subject)
  action: string | null;    // "came to visit", "got the job"
  raw: string;              // original text for fallback
}

function extractEvent(text: string): ExtractedEvent {
  const stripped = text.trim();

  // Pattern 1: "My [relation] [verb phrase]"
  const relMatch = stripped.match(/\bmy\s+(girlfriend|boyfriend|partner|wife|husband|friend|mom|dad|mother|father|brother|sister|son|daughter|cat|dog|pet|boss|teacher|coworker|grandma|grandpa|uncle|aunt|cousin)\s+(.+?)(?:[.!]|$)/i);
  if (relMatch) {
    const rel = relMatch[1].toLowerCase();
    let action = relMatch[2].trim();
    // Clean trailing "me" / "us" / "me yesterday" for echo: "came to visit me" → "came to visit"
    action = action.replace(/\s+(?:me|us|you)(?:\s+(?:yesterday|today|last\s+\w+|this\s+\w+))?\s*$/, "").trim();
    return { subject: `your ${rel}`, action, raw: stripped };
  }

  // Pattern 2: "[ProperNoun] [verb phrase]" — "Sarah got promoted"
  const properMatch = stripped.match(/\b([A-Z][a-z]+)\s+(came|went|got|said|did|made|found|lost|bought|moved|started|finished|called|texted|told|asked|gave|took|left|arrived|visited|surprised|proposed|graduated|passed|won)\b(.*?)(?:[.!]|$)/i);
  if (properMatch) {
    const name = properMatch[1];
    const FUNC_CAPS = new Set(["I", "The", "A", "My", "He", "She", "They", "It", "We", "You",
      "What", "Who", "How", "Why", "When", "Where", "So", "But", "And", "Or", "Today", "Yesterday"]);
    if (!FUNC_CAPS.has(name)) {
      let action = (properMatch[2] + properMatch[3]).trim();
      action = action.replace(/\s+(?:me|us|you)(?:\s+(?:yesterday|today|last\s+\w+|this\s+\w+))?\s*$/, "").trim();
      return { subject: name, action, raw: stripped };
    }
  }

  // Pattern 3: "I [verb phrase]" — "I got the job" / "I passed my exam"
  const selfMatch = stripped.match(/\bI\s+(got|passed|won|graduated|found|started|finished|made|moved|celebrated)\s+(.+?)(?:[.!]|$)/i);
  if (selfMatch) {
    const action = (selfMatch[1] + " " + selfMatch[2]).trim().replace(/\s+(?:me|us)\s*$/, "");
    return { subject: null, action, raw: stripped };
  }

  // Pattern 4: "We [verb phrase]" — "We had an amazing time" / "We're getting married"
  const weMatch = stripped.match(/\bwe(?:'re| are)?\s+(.+?)(?:[.!]|$)/i);
  if (weMatch) {
    const action = weMatch[1].trim();
    return { subject: null, action, raw: stripped };
  }

  // Pattern 5: "[Subject] was/is [exclamation]" — "Today was amazing"
  const exclMatch = stripped.match(/^(.+?)\s+(?:was|is)\s+((?:so |really |incredibly )?(?:amazing|incredible|awesome|wonderful|fantastic|perfect|beautiful|great|lovely|magical))\b/i);
  if (exclMatch) {
    return { subject: null, action: null, raw: stripped };
  }

  return { subject: null, action: null, raw: stripped };
}

// ── Parameterized Event Response Builders ────────────────────────────────
// Instead of static pools, these compose responses from the player's actual words.
// Four structural patterns:
//   echo_query:      "Your girlfriend came to visit? That's huge."
//   statement_expand: "Got the job. That's real. How does it feel?"
//   mirror_forward:   "She came to visit — what was that like?"
//   bridge_action:    "Your mom surprised you. I love that. Tell me everything."

function buildPositiveEventResponse(event: ExtractedEvent, rand: () => number): string {
  const { subject, action } = event;
  if (subject && action) return PC.composeEventPositive(subject, action, rand);
  if (subject) return PC.composeEventPositiveSubject(subject, rand);
  if (action) return PC.composeEventPositiveAction(action, rand);
  return PC.composeEventPositiveFallback(rand);
}

function buildNegativeEventResponse(event: ExtractedEvent, rand: () => number): string {
  const { subject, action } = event;
  if (subject && action) return PC.composeEventNegative(subject, action, rand);
  if (action) return PC.composeEventNegativeAction(action, rand);
  return PC.composeEventNegativeFallback(rand);
}

function buildNeutralEventResponse(event: ExtractedEvent, rand: () => number): string {
  const { subject, action } = event;
  if (subject && action) return PC.composeEventNeutral(subject, action, rand);
  if (action) return PC.composeEventNeutralAction(action, rand);
  return PC.composeEventNeutralFallback(rand);
}

// ── Emotional Complexity Detection ──────────────────────────────────────────
// Messages with real emotional weight that the operator pipeline would mangle
// into "The X. That Y." fragments. These need composed, friend-like responses.

// ─ Emotional Contradiction: two feelings in tension ─────────────────────────
// "I'm happy but also terrified" / "I should be grateful but I'm not"
// "I want to go but I'm scared" / "I love them but I can't stand being around them"
const RE_MIXED_FEELINGS = /\bI(?:'m| am) (\w+) (?:but|yet|and) (?:also |still |simultaneously )?(?:I(?:'m| am) )?(\w+)/i;
const RE_SHOULD_BUT = /\bI(?:'m| am)? (?:should|shouldn't|ought to|supposed to) (?:be |feel )?([\w ]+?) but (?:I(?:'m| am)? (?:not|don't|can't)|I (?:can't|couldn't|won't|don't|feel))/i;
const RE_WANT_BUT = /\bI (?:want|wanted|need|needed) to (.+?) but (?:I(?:'m| am)? |I (?:can't|don't|won't|couldn't))/i;
// Broader relational contradiction: "I love X but I can't Y" / "I care about X but Y"
const RE_RELATIONAL_BUT = /\bI (?:love|care about|miss|like|adore|hate|trust|need|want) (?:them|him|her|you|it|this|that|my \w+|\w+) but (?:I )?(?:can't|don't|won't|couldn't|also|sometimes|still|just)/i;

function detectEmotionalContradiction(text: string, rand: () => number): string | null {
  // Pattern 1: "I'm [A] but [also] [B]" — two explicit feelings
  const mixedMatch = text.match(RE_MIXED_FEELINGS);
  if (mixedMatch) {
    const a = mixedMatch[1].toLowerCase();
    const b = mixedMatch[2].toLowerCase();
    return PC.composeEmotionalContradiction(a, b, rand);
  }

  // Pattern 2: "I should [feel X] but I'm not / I don't / I can't"
  const shouldMatch = text.match(RE_SHOULD_BUT);
  if (shouldMatch) {
    return PC.composeShouldBut(rand);
  }

  // Pattern 3: "I want to [X] but I can't/don't/won't"
  if (RE_WANT_BUT.test(text)) {
    return PC.composeWantBut(rand);
  }

  // Pattern 4: "I love them but I can't stand being around them"
  if (RE_RELATIONAL_BUT.test(text)) {
    return PC.composeRelationalBut(rand);
  }

  return null;
}

// ─ Social Pressure: others dictating how the player should feel ─────────────
// "Everyone says I should be over it by now" / "People keep telling me to move on"
// "My family thinks I'm being dramatic" / "No one understands"
const RE_SOCIAL_PRESSURE = /\b(?:(?:everyone|everybody|people|they|my (?:friends?|family|parents?|mom|dad|sister|brother|partner|wife|husband|therapist|boss|coworkers?)) (?:says?|thinks?|keeps? (?:telling|saying|asking)|told me|wants? me to|expects? me to|(?:don't|doesn't|don't) understand)|(?:no one|nobody) (?:understands?|gets? it|believes? me|listens?|cares?)|(?:everyone|everybody) (?:else )(?:seems?|is |has |can )|I(?:'m| am) (?:the only one|always the one) (?:who|that))/i;

function detectSocialPressure(text: string, rand: () => number): string | null {
  if (!RE_SOCIAL_PRESSURE.test(text)) return null;

  // "Nobody understands" / "No one gets it" — isolation, not pressure
  if (/\b(?:no one|nobody) (?:understands?|gets? it|believes?|listens?|cares?)\b/i.test(text)) {
    return PC.composeSocialPressureIsolation(rand);
  }

  // "Everyone else seems to have it figured out" — comparison
  if (/\b(?:everyone|everybody) (?:else )(?:seems?|is |has |can )/i.test(text)) {
    return PC.composeSocialPressureComparison(rand);
  }

  // External pressure — "everyone says", "they think", "my family wants me to"
  return PC.composeSocialPressureExternal(rand);
}

// ─ Emotional Overflow: involuntary emotional expression ──────────────────────
// "I keep almost crying at random times" / "I can't stop thinking about it"
// "I don't know why I'm so angry" / "I keep having these dreams"
const RE_EMOTIONAL_OVERFLOW = /\b(?:I (?:keep|can't stop|won't stop|couldn't stop) (?:almost |nearly )?(?:crying|tearing up|getting (?:emotional|angry|upset|anxious|overwhelmed)|thinking about|dreaming about|going back to|remembering|replaying|feeling|wanting to (?:cry|scream|yell|run|leave|hide|throw up|disappear))|I (?:don't know|can't figure out|can't understand|can't explain) why I(?:'m| am) (?:so )?(?:angry|sad|upset|emotional|anxious|scared|irritable|on edge|crying|shaking|overwhelmed|numb))/i;

function detectEmotionalOverflow(text: string, rand: () => number): string | null {
  if (!RE_EMOTIONAL_OVERFLOW.test(text)) return null;

  // "Can't stop thinking/going back/remembering" — cognitive rumination
  if (/\b(?:can't stop|keep|won't stop) (?:thinking|going back|remembering|replaying|dreaming)\b/i.test(text)) {
    return PC.composeEmotionalOverflowRumination(rand);
  }

  // "Don't know why I'm so [emotion]" — unnamed source
  if (/\bdon't know why\b/i.test(text)) {
    return PC.composeEmotionalOverflowUnnamed(rand);
  }

  // General overflow — "keep almost crying", "can't stop getting emotional"
  return PC.composeEmotionalOverflowGeneral(rand);
}

// ─ Emotional Uncertainty: can't name or distinguish what they feel ────────────
// "I don't know if I'm sad or just tired" / "I don't know what I'm feeling"
// "Something feels off but I can't figure out what"
const RE_EMOTIONAL_UNCERTAINTY = /\b(?:I (?:don't|do not|can't|cannot) know (?:if I(?:'m| am) |what I(?:'m| am) |how I(?:'m| am)? |what (?:to|I) |how to )(?:feel|feeling|doing|processing|supposed to|sad|happy|angry|upset|okay|fine)|(?:something|everything) feels? (?:off|wrong|weird|different|strange|heavy|empty|numb|flat|hollow) (?:but|and) I (?:don't|can't|cannot)|I feel\.{2,}|I feel (?:something|nothing|numb|empty|flat|blank|hollow|off|weird|strange) (?:but )?I (?:don't|can't|cannot)|I(?:'m| am) (?:not sure|unsure) (?:what|how|if) I(?:'m| am)? (?:feel|feeling)|I (?:can't|cannot|don't) (?:even )?(?:tell|say|figure out|explain|describe|name|put into words) (?:what|how) I(?:'m| am)? (?:feel|feeling))/i;

function detectEmotionalUncertainty(text: string, rand: () => number): string | null {
  if (!RE_EMOTIONAL_UNCERTAINTY.test(text)) return null;

  // "Sad or just tired" — binary choice offered
  const orMatch = text.match(/(?:if I(?:'m| am)) (\w+) or (?:just |only )?(\w+)/i);
  if (orMatch) {
    const a = orMatch[1].toLowerCase();
    const b = orMatch[2].toLowerCase();
    return PC.composeEmotionalUncertaintyOr(a, b, rand);
  }

  return PC.composeEmotionalUncertaintyGeneral(rand);
}

// ─ Denial / Deflection: "I'm fine" protest pattern ───────────────────────────
// "it's fine. everything's fine. I'm fine." — repeating fine/okay signals NOT fine
// "I don't care" / "it doesn't matter" / "whatever, it's nothing"
const RE_PROTEST_FINE = /\b(?:(?:I(?:'m| am) fine|it(?:'s| is) fine|everything(?:'s| is) fine|I(?:'m| am) (?:okay|ok|alright|good)).*?(?:I(?:'m| am) fine|it(?:'s| is) fine|everything(?:'s| is) fine|I(?:'m| am) (?:okay|ok|alright|good))|(?:fine|okay|ok|alright)\.?\s+(?:fine|okay|ok|alright|everything|it'?s|I'?m))/i;
const RE_DISMISSIVE_DENIAL = /\b(?:I (?:don't|do not) (?:care|even care|give a)|it (?:doesn't|does not|didn't) (?:matter|bother me|affect me|hurt)|(?:it's|it is|that's|that is) (?:nothing|whatever|not a big deal|not important|no big deal|fine really)|(?:I said I(?:'m| am)|I told you I(?:'m| am)|I already said I(?:'m| am)) (?:fine|okay|ok|alright|good|over it))\b/i;

function detectDenialDeflection(text: string, rand: () => number): string | null {
  if (RE_PROTEST_FINE.test(text)) {
    return PC.composeDenialProtestFine(rand);
  }
  if (RE_DISMISSIVE_DENIAL.test(text)) {
    return PC.composeDenialDismissive(rand);
  }
  return null;
}

// ─ Dream Sharing: "I had a weird dream last night" ───────────────────────────
const RE_DREAM_SHARING = /\b(?:I (?:had|have|keep having) (?:a |this |the |these )?(?:really |very |super |so )?(?:weird|strange|crazy|vivid|recurring|bad|good|nice|intense|wild|messed up|fucked up)?\s*(?:dreams?|nightmares?)|I (?:dreamt|dreamed) (?:about |that )|(?:in my dream|in the dream|this dream I had)|I keep (?:having |getting )(?:this |these |the same )?(?:dreams?|nightmares?))\b/i;

function detectDreamSharing(text: string, rand: () => number): string | null {
  if (!RE_DREAM_SHARING.test(text)) return null;
  if (/nightmare/i.test(text)) {
    return PC.composeDreamNightmare(rand);
  }
  return PC.composeDreamGeneral(rand);
}

// ── Mundane / State Sharing Detection ─────────────────────────────────────
// The 18-gap problem: casual messages that VERSE transforms into word-games.
// "I went to the store" → "The going in that." NO. A friend says "How was it?"
// Detects: daily activities, state reports, observations, opinions, plans,
// sarcasm/irony, boundaries/refusal. Low-intensity messages that need casual
// engaged responses, not contemplative depth.

// State/activity sharing: "I went to the store" / "Work has been intense" / "I ate too much"
// Also: simple state declarations like "I'm okay" / "I'm fine" / "I'm good"
const RE_STATE_SHARING = /\b(?:I (?:went|walked|drove|ran|got|took|made|ate|cooked|cleaned|watched|played|read|slept|woke|bought|fixed|finished|called|texted|started|stopped|tried|worked|studied|practiced)\b|work (?:has been|was|is)|school (?:has been|was|is)|traffic (?:was|is)|(?:the|my) (?:house|apartment|place|room|car|phone|computer|dog|cat) (?:is|was|has been)|I(?:'m| am) (?:okay|ok|fine|good|alright|doing okay|doing fine|doing alright|doing good))$/i;

// Mundane obligation: "I need to do laundry" / "I gotta clean the house" / "I should go to the gym"
// "gotta/hafta" don't use "to": "I gotta clean" not "I gotta to clean"
const RE_MUNDANE_OBLIGATION = /\bI (?:(?:need|have|got|should|ought) to |(?:gotta|hafta) )(?:do (?:the |my |some )?(?:laundry|dishes|cleaning|groceries|shopping|homework|taxes|cooking|ironing|vacuuming|packing|washing|filing)|go (?:to (?:the |a )?)?(?:the |a )?(?:store|shop|mall|gym|dentist|doctor|post office|bank|supermarket|laundromat|mechanic|pharmacy|vet|grocery|DMV)|clean (?:the |my |up (?:the |my )?)?(?:house|room|apartment|kitchen|bathroom|car|desk|closet|garage)|pay (?:the |my )?(?:bills?|rent|taxes|utilities)|(?:pick up|drop off|take out|sort out|deal with|wash|mow|fix|fold|iron|vacuum|organize|pack|unpack|return|schedule|cancel|renew|refill))\b/i;

// Physical complaints: "My back hurts" / "I have a headache" / "I'm so tired" / "I'm so done with today"
const RE_PHYSICAL_COMPLAINT = /\b(?:my (?:back|head|neck|knee|shoulder|stomach|throat|feet|leg|arm|hand|eye|tooth|chest) (?:hurts?|aches?|is (?:killing|sore|stiff|tight|bothering))|I (?:have|got|had) (?:a |an? )?(?:headache|migraine|cold|flu|fever|sore throat|cough|stomachache|backache|cramp|rash)|I(?:'m| am) (?:so |really |kinda |super )?(?:tired|exhausted|wiped|drained|sore|stiff|achy|nauseous|dizzy|sick|done (?:with|about) (?:today|this|everything|it all|all of this))|(?:can't|cannot) (?:sleep|breathe|focus|see|hear|move|walk)|I(?:'m| am) (?:so |really |just )?(?:over (?:it|today|this|everything)|done (?:with|about)))/i;

// Daily frustration: "I can't find my keys" / "The neighbor's dog won't stop barking"
const RE_DAILY_FRUSTRATION = /\b(?:(?:I )?(?:can't|cannot|couldn't) (?:find|remember|figure out|get|reach|open|fix|make|stop)|(?:won't|will not) (?:stop|start|work|open|close|turn on|turn off|connect|load|respond)|(?:the|my) (?:\w+\s+)?(?:won't|doesn't|isn't|keeps?|always|never) (?:stop|work|respond|connect|charge|start))/i;

// Intellectual sharing: "I read this article about X" / "I've been getting into philosophy"
// Also: "There's this thing called X" / "Have you heard of X" / "Do you know about X"
const RE_INTELLECTUAL_SHARING = /\b(?:I (?:read|saw|heard|found|watched|listened to) (?:this|an?|the|some) (?:article|book|podcast|video|documentary|study|paper|post|thing|tweet|thread)|I(?:'ve| have) been (?:getting into|reading about|learning about|thinking about|interested in|researching|studying|watching|listening to)|there(?:'s| is) (?:this|a) (?:thing|concept|idea|theory|question|paradox|experiment|phenomenon|hypothesis|principle) (?:called|about|where|that)|(?:have you|you ever) (?:heard (?:of|about)|read about|seen|thought about|come across)|(?:do you|you) know (?:what|about) (?:the |a )?(?:\w+ ){0,3}(?:hypothesis|paradox|theory|principle|effect|problem|experiment|phenomenon|equation|theorem|conjecture|dilemma|fallacy|law|rule|concept))\b/i;

// Opinion sharing: "I think pineapple on pizza is fine" / "That movie was overrated"
const RE_OPINION_SHARING = /\b(?:I (?:think|believe|feel like|reckon|honestly think|personally think)\b|(?:is|was|are|were) (?:overrated|underrated|fine|okay|alright|mid|trash|garbage|amazing|the best|the worst|kinda|kind of|pretty|not that|not really)|(?:honestly|personally|in my opinion|imo|tbh|lowkey|highkey))/i;

// Future/plans: "I'm thinking of going to Japan" / "I might try that new place"
const RE_FUTURE_PLANS = /\b(?:I(?:'m| am) (?:thinking (?:of|about)|planning (?:to|on)|gonna|going to|hoping to|considering|looking into)|I might|I may|I want to (?:try|go|visit|start|check out|see|do)|maybe I(?:'ll| will)|I should (?:probably |really )?(?:go|try|do|start|check|look))\b/i;

// Sarcasm/irony: "Oh great, another Monday" / "Wow, what a surprise" / "Love that for me"
const RE_SARCASM = /\b(?:oh (?:great|wonderful|fantastic|lovely|perfect|joy|goody|yay|how nice),?\s|wow,?\s+(?:what a|how|that's|so)|love that for (?:me|us)|another (?:one|day|monday|meeting|fun)|just (?:what I needed|my luck|great|perfect|wonderful)|(?:what a|how) (?:surprise|shock|treat|delight)|can't wait (?:for|to)\b.*?(?:\bsaid no one\b|\.{2,}|$))/i;

// Boundary/refusal: "I don't want to talk about it" / "Can we change the subject"
const RE_BOUNDARY = /\b(?:I (?:don't|do not|can't|cannot) (?:want|wanna) to (?:talk|think|go there|discuss|get into)|(?:can|could) we (?:change|switch|move on|talk about something|not)|I(?:'d| would) rather not|let(?:'s| us) (?:not|move on|change|drop|skip)|drop it|leave it|not (?:now|today|right now)|I(?:'m| am) not (?:ready|up for|in the mood))\b/i;

// Observation/weather: "The sky looks weird" / "It's hot out" / "Everything feels off"
const RE_OBSERVATION = /\b(?:the (?:sky|weather|air|sun|moon|rain|wind|clouds?|snow|fog|stars?|light|night|morning|evening|silence|quiet) (?:looks?|feels?|seems?|is|was|smells?|sounds?)|(?:rain|wind|thunder|snow|music|silence|birds?|crickets?|waves?) (?:sounds?|feels?|smells?) (?:nice|good|great|amazing|beautiful|peaceful|calming|soothing|weird|eerie|loud|soft|different|familiar)|it(?:'s| is) (?:so |really |super )?(?:hot|cold|warm|freezing|humid|windy|raining|snowing|foggy|cloudy|nice|beautiful|gorgeous|gray|gloomy|dark|quiet|peaceful|still) (?:out|outside|today|tonight|right now)?|everything (?:feels?|looks?|seems?|is) (?:off|weird|different|strange|quiet|still|heavy|light|slow)|(?:that|this|it) (?:is|was|'s) (?:so |really |super )?(?:annoying|frustrating|infuriating|ridiculous|absurd|unfair|stupid|dumb|ridiculous))\b/i;

function detectMundaneSharing(text: string, intensity: number, rand: () => number): string | null {
  // Only catch low-to-mid intensity — high intensity mundane text might have hidden depth
  if (intensity > 0.55) return null;

  // Don't catch questions — those need different handling
  if (text.endsWith("?")) return null;

  // Don't catch long messages — 15+ words is likely complex emotional content,
  // not mundane sharing. "I couldn't remember the last time that happened" in a
  // 22-word sentence is NOT daily frustration.
  const mundaneWordCount = text.split(/\s+/).filter(Boolean).length;
  if (mundaneWordCount >= 15) return null;

  // ── Physical complaint: empathy, not analysis ──
  if (RE_PHYSICAL_COMPLAINT.test(text)) {
    return PC.composeMundanePhysical(text, rand);
  }

  // ── Daily frustration: commiserate, don't analyze ──
  if (RE_DAILY_FRUSTRATION.test(text)) {
    return PC.composeMundaneFrustration(text, rand);
  }

  // ── Intellectual sharing: genuine curiosity about what they learned ──
  if (RE_INTELLECTUAL_SHARING.test(text)) {
    return PC.composeContinuousThinking(text, rand);
  }

  // ── Boundary/refusal: respect immediately, no probing ──
  if (RE_BOUNDARY.test(text)) {
    return PC.composeMundaneBoundary(text, rand);
  }

  // ── Sarcasm/irony: match the tone, don't be earnest about it ──
  if (RE_SARCASM.test(text)) {
    return PC.composeMundaneSarcasm(text, rand);
  }

  // ── Future/plans: genuine curiosity, not analysis ──
  if (RE_FUTURE_PLANS.test(text)) {
    return PC.composeMundaneFuturePlans(text, rand);
  }

  // ── Opinion sharing: engage with the opinion, don't reflect it ──
  if (RE_OPINION_SHARING.test(text)) {
    return PC.composeMundaneOpinion(text, rand);
  }

  // ── Pop culture / aesthetic: sharing something that moved them ──
  if (/\b(?:this (?:song|show|movie|book|video|podcast|game|album|band|artist) (?:is|has been|keeps|just|really|hits?|slaps?|goes|makes?|gets?)|I(?:'ve| have) been (?:listening to|watching|binging|hooked on|obsessed with|into)|(?:have you )?(?:seen|heard|watched|listened to|read) (?:the new|that new|this new)|I (?:love|really love|adore|am (?:obsessed|into|loving)) (?:this|that) (?:song|show|movie|book|video|podcast|game|album|band|artist|track|episode|season))\b/i.test(text)) {
    return PC.composeMundanePopCulture(text, rand);
  }

  // ── Seasonal / environmental affection ──
  if (/\bI (?:love|really love|genuinely love|actually love) (?:fall|autumn|winter|spring|summer|the rain|thunderstorms|snow|this weather|this time of year)\b/i.test(text) ||
      /\b(?:fall|autumn|winter|spring|summer|the rain|snow|this weather|this time of year) (?:always|usually|never|sometimes) (?:makes?|gets?) me (?:feel |feel like )?\b/i.test(text)) {
    return PC.composeMundaneSeasonal(text, rand);
  }

  // ── Observation: match the noticing energy ──
  if (RE_OBSERVATION.test(text)) {
    return PC.composeMundaneObservation(text, rand);
  }

  // ── Mundane obligation: casual chore/errand sharing ──
  if (RE_MUNDANE_OBLIGATION.test(text)) {
    // Extract what they need to do for specificity
    const taskMatch = text.match(/to (?:do (?:the |my |some )?)?(\w+)/i);
    const task = taskMatch ? taskMatch[1].toLowerCase() : null;
    if (task && /^(?:laundry|dishes|cleaning|groceries|shopping|cooking|ironing|vacuuming|packing)$/.test(task)) {
      return PC.composeMundaneObligation(task, rand);
    }
    return PC.composeMundaneObligation(null, rand);
  }

  // ── State/activity sharing: casual engagement ──
  if (RE_STATE_SHARING.test(text)) {
    // State declarations like "I'm okay" / "I'm fine" — gentle check-in
    if (/\bI(?:'m| am) (?:okay|ok|fine|good|alright|doing okay|doing fine|doing alright|doing good)\s*[.!?]*$/i.test(text.trim())) {
      return PC.composeStateDeclaration(text, rand);
    }
    // Extract what they did for a more specific response
    const actMatch = text.match(/I\s+(went|walked|drove|ran|got|took|made|ate|cooked|cleaned|watched|played|read|slept|woke|bought|fixed|finished|called|texted|started|stopped|tried|worked|studied|practiced)\s+(.+?)(?:[.!,]|$)/i);
    if (actMatch) {
      const verb = actMatch[1].toLowerCase();
      const object = actMatch[2].trim().replace(/\s+(?:and|but)\s+.*$/, "").substring(0, 40);
      // Verb-specific responses
      if (/^(?:ate|cooked|made)$/i.test(verb)) {
        return PC.composeMundaneActivity(verb, object, rand);
      }
      if (/^(?:watched|played|read)$/i.test(verb)) {
        return PC.composeMundaneActivity(verb, object, rand);
      }
      return PC.composeMundaneActivityGeneric(object, rand);
    }

    // State report without extractable action
    return PC.composeMundaneStateReport(text, rand);
  }

  return null;
}

// ── Social Register Detection ──────────────────────────────────────────────
// Introductions, narrative about people, entity recall — conversational modes
// that VERSE should never process through kernel extraction.

// Introduction MUST contain a proper noun (capital letter, case-sensitive check).
// Can't use /i flag or the [A-Z] guard is useless.
const RE_INTRODUCTION_KEYWORDS = /\b(?:introduce|introducing|meet|this is)\b/i;
const RE_INTRODUCTION_RELATIONAL = /\bmy (?:friend|brother|sister|mom|dad|mother|father|husband|wife|partner|colleague|coworker|boss|son|daughter|uncle|aunt|cousin|grandma|grandpa|girlfriend|boyfriend)\s+([A-Z][a-z]+)\b/;
// Reverse: "[ProperNoun] is my [relation]" — most natural introduction form
const RE_INTRODUCTION_REVERSE = /\b([A-Z][a-z]+)\s+(?:is|was)\s+my\s+(?:friend|brother|sister|mom|dad|mother|father|husband|wife|partner|colleague|coworker|boss|son|daughter|uncle|aunt|cousin|grandma|grandpa|girlfriend|boyfriend)\b/i;

function isIntroduction(text: string): boolean {
  // "introduce/meet/this is" + any proper noun
  if (RE_INTRODUCTION_KEYWORDS.test(text) && /\b[A-Z][a-z]{1,}\b/.test(text.replace(/^[A-Z]\S*\s+/, ""))) return true;
  // "my [relation] [ProperNoun]" — case-sensitive proper noun check
  if (RE_INTRODUCTION_RELATIONAL.test(text)) return true;
  // "[ProperNoun] is my [relation]" — reverse form: "John is my friend"
  if (RE_INTRODUCTION_REVERSE.test(text)) return true;
  return false;
}

// Case-insensitive prefix, but the captured name MUST start with a real capital.
// (No /i flag — that would let "the meaning" match as a proper name.)
const RE_ENTITY_RECALL = /\b(?:[wW]ho is|[wW]ho's|[tT]ell me about|[wW]hat do you know about|[wW]hat about|[rR]emember|[wW]hat is|[wW]hat does|[wW]hat did|[wW]hat can)\s+([A-Z][a-z]+)\b/;

// Skeptical / challenge register: short pushback on the Aeterna's last response
const RE_SKEPTICAL = /^(?:did (?:it|you) really\??|really\??|are you sure\??|you think\??|do you mean that\??|seriously\??|for real\??|you believe that\??|is that true\??)$/i;

// "Why" / "What" as bare one-word challenge (not "why did X happen")
const RE_BARE_CHALLENGE = /^(?:why\??|what\??|how\??|huh\??)$/i;

// ── Gender inference from relationship words ──────────────────────────────
const FEMININE_RELS = new Set([
  "girlfriend", "wife", "mother", "mom", "sister", "daughter", "grandma",
  "grandmother", "aunt", "niece",
]);
const MASCULINE_RELS = new Set([
  "boyfriend", "husband", "father", "dad", "brother", "son", "grandpa",
  "grandfather", "uncle", "nephew",
]);

type Pronouns = { them: string; they: string; their: string };
function inferPronouns(text: string): Pronouns {
  const lower = text.toLowerCase();
  // Check for explicit pronouns in the text first
  if (/\bshe\b|\bher\b/.test(lower)) return { them: "her", they: "she", their: "her" };
  if (/\bhe\b|\bhis\b/.test(lower)) return { them: "him", they: "he", their: "his" };
  // Check relationship words
  for (const rel of FEMININE_RELS) {
    if (lower.includes(rel)) return { them: "her", they: "she", their: "her" };
  }
  for (const rel of MASCULINE_RELS) {
    if (lower.includes(rel)) return { them: "him", they: "he", their: "his" };
  }
  // Default: gender-neutral
  return { them: "them", they: "they", their: "their" };
}

function makeSocialIntroResponse(text: string, rand: () => number): string {
  // Extract the name from the text
  const nameMatch = text.match(/\b([A-Z][a-z]+)\b/g);
  const FUNC_CAPS = new Set(["I", "The", "A", "My", "He", "She", "They", "It", "We", "You",
    "What", "Who", "How", "Why", "When", "Where", "Hey", "Hello", "Hi",
    "Yeah", "Did", "Do", "Is", "Are", "Was", "But", "And", "Or", "So",
    "Not", "Just", "Really", "Also", "This", "That"]);
  const name = (nameMatch ?? []).find(n => !FUNC_CAPS.has(n)) ?? "them";
  const p = inferPronouns(text);

  return PC.composeSocialIntro(name, p, rand);
}

function entityPronouns(entity: { relationship?: string | null } | null): Pronouns {
  const rel = entity?.relationship?.toLowerCase() ?? "";
  if (FEMININE_RELS.has(rel)) return { them: "her", they: "she", their: "her" };
  if (MASCULINE_RELS.has(rel)) return { them: "him", they: "he", their: "his" };
  return { them: "them", they: "they", their: "their" };
}

function makeEntityRecallResponse(name: string, rand: () => number): string | null {
  const entity = getEntity(name);
  if (!entity) return null;

  const rel = entity.relationship ? `Your ${entity.relationship}` : name;
  const p = entityPronouns(entity);
  const descs = entity.descriptors.filter(d => d.length > 2).map(d => formatDescWithPronouns(d, p));

  if (descs.length === 0) {
    return PC.composeEntityRecallNoDesc(name, rel, p, rand);
  }

  const desc = descs[0];
  return PC.composeEntityRecallWithDesc(name, rel, desc, rand);
}

// ── Information Intake: player providing facts about a known entity ─────────
// "He's a Ninja with VR" / "She works at the hospital" / "John is really smart"
// These need ENGAGED, CURIOUS responses — not emotional transformation.

const RE_PRONOUN_INFO = /^(he|she|they)\b/i;
const RE_ENTITY_INFO = /\b([A-Z][a-z]+)\s+(is|was|works?|does|makes?|builds?|plays?|loves?|has|creates?|knows?|lives?|studies|codes?|designs?|writes?|teaches?)\b/i;

/**
 * Truncate a clause for echo — if it's too long to echo verbatim,
 * take the first meaningful chunk. "super beautiful and cute and chubby and I call her my kitten"
 * → "super beautiful and cute and chubby" (first ~5 words or first "and I" break).
 */
function truncateForEcho(clause: string, maxWords: number = 6): string {
  // First: strip "and I [verb]" clauses — those are about the player, not the entity
  let stripped = clause.replace(/\s*(?:,?\s*and)?\s+(?:I|we)\s+(?:call|named?|think|say|love|like|adore|consider)\s+.*/i, "").trim();
  // Strip trailing punctuation
  stripped = stripped.replace(/[,;:.!?]+$/, "").trim();
  const words = stripped.split(/\s+/);
  if (words.length <= maxWords) return stripped;
  // Truncate to maxWords, try to end at a natural boundary (and, comma)
  let cut = words.slice(0, maxWords).join(" ");
  // If the last word is "and", drop it
  cut = cut.replace(/\s+and\s*$/, "");
  // Strip trailing punctuation from truncated result
  cut = cut.replace(/[,;:.!?]+$/, "").trim();
  return cut;
}

function makeInfoIntakeResponse(text: string, entityName: string | null, rand: () => number): string {
  const entity = entityName ? getEntity(entityName) : null;
  const descs = entity?.descriptors ?? [];
  const displayName = entity?.name ?? entityName ?? "them";

  // Extract what the player JUST said — all clauses, preserving casing
  const clauses: string[] = [];

  // Split ONLY on conjunction + pronoun restart (", but he" / ", and she")
  // NOT on bare "and" which splits "VR and Game Engine" into fragments
  const parts = text.split(/\s*(?:,\s*(?:but|and|plus|also)\s+(?=(?:he|she|they)\b)|\s+but\s+(?=(?:he|she|they)\b))\s*/i);
  for (const part of parts) {
    // "He's [desc]" / "He is [desc]"
    const isMatch = part.match(/(?:he|she|they|[A-Z][a-z]+)(?:'s|'re|\s+(?:is|are|was))\s+(.+?)(?:[.!]|$)/i);
    if (isMatch) { clauses.push(isMatch[1].trim()); continue; }
    // "He [verbs] [stuff]"
    const vMatch = part.match(/(?:he|she|they)\s+(\w+s?\s+.+?)(?:[.!]|$)/i);
    if (vMatch) clauses.push(vMatch[1].trim());
  }

  // Truncate long clauses for echo — prevents verbatim parroting of 60+ char predicates
  const truncatedClauses = clauses.map(c => truncateForEcho(c));

  // Pick the most specific/interesting clause (longest with real content)
  const bestClause = truncatedClauses.sort((a, b) => b.length - a.length)[0] ?? null;
  // Find a secondary clause for multi-clause richness
  const secondClause = truncatedClauses.length > 1 ? truncatedClauses.find(c => c !== bestClause) : null;

  const hasHistory = descs.length > 0;

  if (bestClause && secondClause) {
    return PC.composeInfoIntakeMultiClause(bestClause, secondClause, displayName, rand);
  }

  if (bestClause && hasHistory) {
    const bestLower = bestClause.toLowerCase();
    const priorDesc = descs.find(d => {
      const dl = d.toLowerCase();
      return !dl.includes(bestLower) && !bestLower.includes(dl);
    });
    if (!priorDesc) {
      return PC.composeInfoIntakeClauseOnly(bestClause, displayName, rand);
    }
    return PC.composeInfoIntakeClauseWithHistory(bestClause, priorDesc, displayName, rand);
  }

  if (bestClause) {
    return PC.composeInfoIntakeClauseNew(bestClause, displayName, rand);
  }

  return PC.composeInfoIntakeFallback(displayName, rand);
}

function cap(s: string): string {
  if (!s) return s;
  return s[0].toUpperCase() + s.slice(1);
}

// ── Philosophical Register ─────────────────────────────────────────────────
// "What's the purpose of life?" / "Why do we exist?" / "What happens when we die?"
// These deserve genuine engagement, not emotional transformation of "purpose" → "purposing".

const RE_PHILOSOPHICAL = /\b(purpose|meaning|point) of (life|existence|living|it all|everything)\b|\bwhy (do|are) we (here|alive|exist)\b|\bwhat happens when (we|you|I) die\b|\bwhat('s| is) (the point|the meaning|it all (for|about))\b|\bwhy (does anything|do we|is there) (matter|exist)\b|\b(?:do|does|is|are) (?:\w+ )?(?:real|exist|alive|conscious|sentient|infinite|possible|true|predetermined|free)\??\s*$|\bwhat (?:do you|would you|'?d you) think about (?:free will|consciousness|the meaning of life|death|god|the universe|reality|existence|fate|destiny|morality|ethics|truth|beauty|time|infinity|nothing(?:ness)?|the afterlife|the soul|karma|reincarnation)\b/i;

// PHILOSOPHICAL_RESPONSES removed — now generative via PC.composePhilosophical()

// ── Deep Pattern Detection ──────────────────────────────────────────────────
// Patterns that VERSE operators would mangle into word-games but need
// specific composed responses. Each pattern maps to a compose function
// that produces a friend-like, contextually appropriate response.

// Identity / Self-Discovery
const RE_SELF_DISCOVERY = /\bI (?:just |recently |finally )?(?:realized|noticed|figured out|discovered|learned|understood|saw|found out|came to (?:terms|understand|realize)|accepted) (?:that )?(?:I |my |about )/i;
const RE_USED_TO_BE = /\bI (?:used to (?:be|feel|think|believe|want|love|hate|have|do|go|say|act)|was (?:so |really |always |once )?(?:different|happy|confident|outgoing|quiet|shy|wild|reckless|careless|careful|optimistic|idealistic|naive))\b/i;
const RE_ALWAYS_BEEN = /\bI(?:'ve| have) (?:always|never|my whole life|since I was (?:a kid|young|little)) (?:been|felt|thought|wanted|had|known|struggled|dealt)\b/i;
const RE_ALWAYS_WANTED = /\bI(?:'ve| have) always (?:wanted|dreamed|wished|hoped|longed|yearned|fantasized) (?:to |about |for )/i;
const RE_PROUD_OF = /\bI(?:'m| am) (?:so |really |actually |genuinely |kind of )?(?:proud of (?:myself|me|what I|how I|that I)|proud (?:that|because) I)\b/i;

// Temporal / Emotional State
const RE_REGRET = /\bI (?:wish I (?:had|hadn't|could have|would have|didn't)|should(?:'ve| have) (?:never |not |said|done|been|gone|stayed|left|told|called|asked)|regret (?:not |that I |what I ))\b/i;
const RE_NOSTALGIA = /\bI (?:miss (?:how|when|the way|being|those|that|it)|think about (?:the old|back when|how (?:things|it) used to)|remember when (?:we|I|things|everything|life)|long for|(?:wish|want) (?:I could go|things were|it was still) back)\b/i;
const RE_LIFE_TRANSITION = /\b(?:everything (?:is|feels|seems) (?:different|changing|shifting|moving|falling apart|coming together)|I(?:'m| am) (?:in (?:a )?(?:new|different|weird|strange|transitional) (?:place|phase|chapter|season|stage)|going through (?:a lot|changes|it|something|a transition))|my (?:whole )?life (?:is|has been|just) (?:changing|different|upside down|shifting|flipped)|(?:nothing|things) (?:feel|are) the same (?:anymore|now|lately))\b/i;
const RE_VAGUE_HEADSPACE = /\bI(?:'m| am) (?:in (?:a )?(?:weird|strange|dark|bad|funny|off|low|heavy) (?:place|headspace|mood|space|state|funk|hole)|(?:just )?(?:off|weird|not (?:myself|okay|right|great|good)|out of it|in a funk|going through it|not doing (?:well|great|good)))\b/i;
const RE_BURNOUT = /\bI(?:'m| am) (?:so |really |completely |totally |just )?(?:burned out|burnt out|exhausted|drained|depleted|running on (?:empty|fumes)|at (?:my|the) (?:limit|breaking point|end)|over(?:worked|whelmed|loaded|stretched))\b|\b(?:burnout|burn(?:-| )?out) (?:is|has been|feels?)\b/i;
const RE_EMOTIONAL_DISTRESS = /\b(?:I (?:can't (?:do this|take (?:it|this)|handle (?:it|this|anymore))|(?:want|need) to (?:disappear|die|not exist|vanish|run away|escape|give up|quit|stop (?:existing|being (?:here|alive))))|(?:nobody|no one) (?:would (?:care|notice|miss me)|cares)|(?:what's the point|why (?:am I|do I) (?:even|still) (?:here|trying|bother))|I(?:'m| am) (?:done|finished|over it all|ready to (?:give up|quit|end it|stop trying)))\b/i;
const RE_POSITIVE_EMOTION = /\bI(?:'m| am) (?:so |really |genuinely |actually )?(?:happy|excited|grateful|blessed|thankful|thrilled|stoked|pumped|elated|overjoyed|ecstatic|delighted|content|at peace|in love|on top of the world|feeling (?:good|great|amazing|wonderful|fantastic|incredible))\b/i;

// Continuous Action Patterns
const RE_CONTINUOUS_TRYING = /\bI(?:'ve| have) been (?:trying|working on|attempting|practicing|pushing myself|forcing myself|making myself|learning) (?:to |so hard |really hard )/i;
const RE_CONTINUOUS_PRESENT = /\bI(?:'ve| have) been (?:thinking|wondering|noticing|struggling|dealing|going through|processing|working through|sitting with|carrying|holding|avoiding|dreading|putting off)\b/i;

// Desire / Escape / Avoidance
const RE_CRAVING = /\bI (?:just )?(?:want|need|crave|miss|long for|wish I (?:had|could have)) (?:a |some |to (?:feel|be|have|eat|sleep|rest|relax|escape|leave|quit|stop))\b/i;
const RE_HYPOTHETICAL_ESCAPE = /\b(?:what if I (?:just |simply )?(?:left|quit|dropped|disappeared|moved|ran|walked away|stopped|gave up|said (?:fuck|screw) it)|(?:I )?(?:sometimes |often )?(?:think|wonder|fantasize|daydream) about (?:just |simply )?(?:leaving|quitting|dropping|disappearing|moving|running|walking away|starting over))\b/i;
const RE_AVOIDANCE = /\bI(?:'ve| have) been (?:avoiding|putting off|ignoring|dodging|ducking|delaying|procrastinating|dreading|stalling|sidestepping)\b|\bthere(?:'s| is) (?:a |this )?(?:conversation|thing|talk|discussion) I(?:'ve| have) been (?:avoiding|putting off|needing to have|dreading)\b/i;
const RE_COMPARING = /\b(?:everyone (?:else |around me )?(?:seems?|appears?|looks?) (?:to (?:have|be|know|get)|like they|so much (?:more|better|happier|further))|(?:compared to|next to|unlike) (?:everyone|other people|my (?:friends|peers|colleagues))|(?:I(?:'m| am)|I feel) (?:so )?(?:behind|less than|not enough|inadequate|like a failure|falling behind|left behind|stuck while))\b/i;

// Relational / Entity
const RE_PET_NAME = /\b(?:I call (?:her|him|them)|(?:her|his|their) (?:nickname|pet name) is|(?:we|I) (?:always )?call (?:her|him|them|it)|(?:she|he|they)(?:'s|'re| is| are) (?:my|our) (?:little |baby |sweet |precious )?(?:\w+)(?:,? (?:I|we) call (?:her|him|them)))\b/i;
const RE_UNRESOLVED_PRONOUN = /^(?:(?:they|he|she|someone|this (?:person|guy|girl|woman|man)) (?:hurt|left|betrayed|abandoned|ghosted|rejected|lied|cheated|ignored|used|manipulated|broke|ruined|destroyed|took|stole|said|told|did|made) (?:me|us|my|it|everything))/i;

// Factual / Perception (already handled by RE_FACTUAL and RE_IDENTITY arrays, but composeFact­ualAnswer and composePerception need routes)
// composeFactualAnswer: when the player asks a genuine factual question not about the Aeterna
const RE_FACTUAL_QUESTION = /^(?:what (?:time|day|year|month|date) is|how (?:old|tall|far|long|many|much) (?:is|are|was|were|do|does)|when (?:is|was|did|does|will)|where (?:is|was|are|were)|(?:is|are|was|were|do|does|did|will|can|could|would|should) (?:it|they|he|she|we|the|a|an|this|that))\b.*\?$/i;

// ── Long-Tail Detectors ──────────────────────────────────────────────────────
// These catch the messages that bypass ALL specific patterns but would
// produce hollow or broken output through VERSE operator chains.

// Slang / reaction words — social signals, not content words
const RE_SLANG_REACTION = /^(?:bruh|bro|oof|yikes|ugh+|welp|rip|sheesh|damn|dang|man|dude|omg|smh|fml|jfc|wtf|mood|same|felt that|big time|real|fr|fr fr|facts?|literally|deadass|no cap|on god|i felt that|that part|it is what it is|whatever|guess so|oh well|meh|eh|i guess|sure whatever|fine whatever|can't complain|fair enough|i mean|i dunno|idk|beats me|nah i'?m good|all good|i'?m chill|we'?re good|no worries|it'?s all good|it'?s fine|no biggie|no big deal|not bad|pretty good|living the dream|same old|you know how it is|okay but (?:like,? )?for real(?: though)?|for real(?: though)?|(?:like )?seriously(?: though)?|right\?*|true|that'?s (?:true|real|fair|valid)|valid|word|bet|say less|noted|gotcha|copy that|roger that|understood|touche|fair point|good point|point taken)\.?!?\??$/i;

// Deflection gates — player wants to talk but testing safety
const RE_DEFLECTION_GATE = /^(?:it'?s complicated|long story|hard to explain|i don'?t (?:even )?know (?:where to start|how to (?:say|explain|put) (?:this|it|into words)|what to say)|you wouldn'?t understand|you won'?t get it|i can'?t explain|nobody gets it|you don'?t get it|i don'?t know how to start|where do i (?:even )?(?:start|begin))\.?$/i;

// Non-aeterna engagement questions — need genuine engagement, not kernel transformation
const RE_PLAYFUL_QUESTION = /^(?:do you think (?!(?:i|I) )\w+ (?:know|understand|realize|feel|see|think|care)|have you ever (?:thought|wondered|noticed|felt|considered)|what(?:'s| is) (?:your|the) (?:favorite|favourite|take|opinion|view|hot take) (?:on|about)|(?:what|how) (?:would|do) you (?:think|feel|believe) (?:about|if)|if you could (?:be|do|go|have|see|meet|change|experience|visit) (?:any|one)).*\?$/i;
const RE_REQUEST_CONTENT = /^(?:tell me (?:something|anything)|say something|give me (?:something|advice|a thought|your (?:take|opinion|thoughts))|what (?:do you know|can you tell me|have you got)|surprise me|entertain me|make me (?:laugh|smile|think|feel (?:better|something)))\b/i;
const RE_HYPOTHETICAL_SELF = /^(?:what would you do|how would you (?:handle|deal with|approach|feel|react)|if you were (?:me|in my (?:shoes|place|position|situation))|what (?:should|would) (?:I|someone) do).*(?:\?|$)/i;

/**
 * Detect deep conversational patterns that need composed responses.
 * Returns a direct response string, or null to continue through VERSE.
 *
 * ORDER MATTERS: More specific patterns before general ones.
 * Emotional distress > burnout > vague headspace.
 * Self-discovery > always-been > continuous-present.
 */
function detectDeepPatterns(text: string, rand: () => number): string | null {
  // NOTE: Emotional distress is checked earlier in preRoute (safety-critical priority).

  // ── Identity / Self patterns ──
  if (RE_SELF_DISCOVERY.test(text)) return PC.composeSelfDiscovery(text, rand);
  if (RE_PROUD_OF.test(text)) return PC.composeProudOf(text, rand);
  if (RE_USED_TO_BE.test(text)) return PC.composeUsedToBe(text, rand);
  if (RE_ALWAYS_WANTED.test(text)) return PC.composeAlwaysWanted(text, rand);
  if (RE_ALWAYS_BEEN.test(text)) return PC.composeAlwaysBeen(text, rand);

  // ── Temporal / Emotional state ──
  if (RE_REGRET.test(text)) return PC.composeRegret(text, rand);
  if (RE_NOSTALGIA.test(text)) return PC.composeNostalgia(text, rand);
  if (RE_BURNOUT.test(text)) return PC.composeBurnout(text, rand);
  if (RE_LIFE_TRANSITION.test(text)) return PC.composeLifeTransition(text, rand);
  if (RE_VAGUE_HEADSPACE.test(text)) return PC.composeVagueHeadspace(text, rand);
  if (RE_POSITIVE_EMOTION.test(text)) return PC.composePositiveEmotion(text, rand);

  // ── Desire / Escape / Avoidance ──
  if (RE_HYPOTHETICAL_ESCAPE.test(text)) return PC.composeHypotheticalEscape(text, rand);
  if (RE_AVOIDANCE.test(text)) return PC.composeAvoidanceConfession(text, rand);
  if (RE_COMPARING.test(text)) return PC.composeComparing(text, rand);
  if (RE_CRAVING.test(text)) return PC.composeCraving(text, rand);

  // ── Continuous action patterns ──
  if (RE_CONTINUOUS_TRYING.test(text)) return PC.composeContinuousTrying(text, rand);
  if (RE_CONTINUOUS_PRESENT.test(text)) return PC.composeContinuousPresentFallback(text, rand);

  // ── Relational / Entity ──
  if (RE_PET_NAME.test(text)) return PC.composePetName(text, rand);
  if (RE_UNRESOLVED_PRONOUN.test(text)) return PC.composeUnresolvedPronoun(text, rand);

  // ── Factual questions (not about Aeterna) ──
  if (RE_FACTUAL_QUESTION.test(text)) return PC.composeFactualAnswer(text, rand);

  return null;
}

// ── Compositional Entity Portrait ──────────────────────────────────────────
// Synthesizes entity knowledge into natural speech, not a database dump.
// The goal: sound like someone recounting what they know about a person,
// connecting details, making observations, showing they've been listening.

/**
 * Format a descriptor for natural speech with correct pronouns.
 * "called kitten" → "you call her kitten"
 * "super beautiful" → "super beautiful"
 */
function formatDescWithPronouns(d: string, p: Pronouns): string {
  if (d.startsWith("called ")) {
    const nickname = d.replace(/^called\s+/, "");
    return `you call ${p.them} ${nickname}`;
  }
  return d;
}

function composeEntityPortrait(name: string, rand: () => number): string | null {
  const entity = getEntity(name);
  if (!entity) return null;
  if (entity.descriptors.length === 0) return null;

  const rel = entity.relationship;
  const p = entityPronouns(entity);
  const descs = entity.descriptors.filter(d => d.length > 2).map(d => formatDescWithPronouns(d, p));
  if (descs.length === 0) return null;

  // ── Synthesis strategies based on descriptor count ──
  // Each strategy produces a DIFFERENT sentence structure, not just
  // concatenated phrases. This is what makes it feel like recall, not lookup.

  if (descs.length === 1) {
    const d = descs[0];
    const frame = rel ? `your ${rel}` : name;
    return PC.composeEntityPortraitSingle(name, frame, d, rand);
  }

  if (descs.length === 2) {
    const [a, b] = descs;
    const frame = rel ? `Your ${rel}` : name;
    return PC.composeEntityPortraitDouble(name, frame, a, b, rand);
  }

  // 3+ descriptors: synthesize with natural connectives, pick 2-3 most distinctive
  // Don't dump everything — choose and connect.
  const short = descs.filter(d => d.split(/\s+/).length <= 4);
  const long = descs.filter(d => d.split(/\s+/).length > 4);
  const featured = [...short.slice(0, 2), ...long.slice(0, 1)].slice(0, 3);

  if (featured.length === 0) return null;

  const frame = rel ? `Your ${rel}` : name;
  const joined = featured.length === 1
    ? featured[0]
    : featured.length === 2
      ? `${featured[0]} — and ${featured[1]}`
      : `${featured[0]}, ${featured[1]} — and ${featured[2]}`;

  return PC.composeEntityPortraitMulti(name, frame, joined, rand);
}

// ── Classifier ──────────────────────────────────────────────────────────────

function classifyRegister(text: string, intensity: number): MessageRegister {
  const stripped = text.trim();

  // Priority 1: META — identity, opinion, factual, understanding, existential
  for (const re of RE_IDENTITY) if (re.test(stripped)) return "meta";
  for (const re of RE_UNDERSTANDING) if (re.test(stripped)) return "meta";
  for (const re of RE_EXISTENTIAL) if (re.test(stripped)) return "meta";
  for (const re of RE_OPINION) if (re.test(stripped)) return "meta";
  for (const re of RE_FACTUAL) if (re.test(stripped)) return "meta";

  // Priority 2: AFFECTIVE_SYMBOL — emoji, hearts, love declarations, pure laughter
  const noWhitespace = stripped.replace(/\s+/g, "");
  if (RE_HEARTS.test(noWhitespace) || RE_EMOJI_ONLY.test(noWhitespace)) return "affective_symbol";
  if (RE_PURE_LAUGHTER.test(noWhitespace)) return "affective_symbol";
  for (const re of RE_DIRECT_LOVE) if (re.test(stripped)) return "affective_symbol";

  // Priority 3: CASUAL — greetings / how-are-you
  if (RE_GREETING.test(stripped) || RE_HOW_ARE_YOU.test(stripped)) {
    // Short greetings (<=4 words) are always casual — "Hello" is never high_affect.
    // Longer messages that OPEN with a greeting ("Hey, I've been thinking about
    // dying") need intensity gating to avoid classifying real content as casual.
    const wordCount = stripped.split(/\s+/).filter(Boolean).length;
    if (wordCount <= 4 || intensity < 0.35) return "casual";
  }

  // Priority 4: Everything else
  return "high_affect";
}

// ── Pre-Router ──────────────────────────────────────────────────────────────

/**
 * Route messages that should NOT go through the full VERSE pipeline.
 * Returns a response string to short-circuit, or null to proceed to VERSE.
 */
function preRoute(ctx: GeneratorContext, rand: () => number): string | null {
  const text = ctx.playerText;
  const stripped = text.trim();
  const intensity = ctx.cortex.immediateAffect.intensity;

  // ── Affective symbols: pure emoji/hearts/laughter ──
  // These have no linguistic content at all — no kernel to extract.
  // Direct response is the only option.
  const register = classifyRegister(text, intensity);
  if (register === "affective_symbol") {
    const noWs = text.trim().replace(/\s+/g, "");
    if (RE_PURE_LAUGHTER.test(noWs)) return PC.composeLaughter(stripped, rand);
    return PC.composeWarmth(stripped, rand);
  }

  // ── Entity management ──
  // These are database operations, not conversational responses.
  // The response references stored data, not the player's words.

  // Player-entity recall: "What do I call X?" / "Who do I love?" / "Do you know who X is?"
  const playerRecallMatch = stripped.match(/\b(?:what do I call|who do I (?:love|like|care about)|do you (?:know|remember) (?:who|what)|who I (?:love|like|care about))\b/i);
  if (playerRecallMatch) {
    const namedMatch = stripped.match(/\b([A-Z][a-z]+)\b/g);
    const FUNC_CAPS = new Set(["I", "The", "A", "My", "He", "She", "They", "It", "We", "You",
      "What", "Who", "How", "Why", "When", "Where", "Do", "Does"]);
    const entityName = namedMatch?.find(n => !FUNC_CAPS.has(n));

    if (entityName) {
      const entity = getEntity(entityName);
      if (entity) {
        const descs = entity.descriptors.filter(d => d.length > 2);
        const rel = entity.relationship ? `your ${entity.relationship}` : entityName;
        const ep = entityPronouns(entity);
        const petName = descs.find(d => d.startsWith("called "));
        if (/what do I call/i.test(stripped) && petName) {
          const name = petName.replace(/^called\s+/, "");
          return PC.composeEntityPetName(name, ep, rand);
        }
        const fmtDescs = descs.filter(d => !d.startsWith("called ")).map(d => formatDescWithPronouns(d, ep));
        if (fmtDescs.length > 0) {
          return PC.composeEntityKnownQuery(entityName, rel, fmtDescs, rand);
        }
        return PC.composeEntityKnownNoDesc(entityName, rel, rand);
      }
    }

    if (/who (?:do )?I (?:love|like|care about)/i.test(stripped)) {
      const allEntities = [...getEntities().values()];
      const romantic = allEntities.find(e =>
        e.relationship && /girlfriend|boyfriend|partner|wife|husband|fianc/i.test(e.relationship));
      if (romantic) {
        const descs = romantic.descriptors.filter(d => d.length > 2 && !d.startsWith("called "));
        const descPart = descs.length > 0 ? ` ${cap(descs[0])}.` : "";
        return PC.composeEntityRomantic(romantic.name, romantic.relationship!, descPart, rand);
      }
      return PC.composeEntityRomanticUnknown(rand);
    }
  }

  // Entity recall: "who is John?" / "tell me about Sarah" / "what is John good at?"
  const entityRecallMatch = stripped.match(RE_ENTITY_RECALL);
  if (entityRecallMatch) {
    const name = entityRecallMatch[1];
    const entity = getEntity(name);
    const rel = entity?.relationship;
    const isPortraitLike = (r: string) =>
      (rel && r.startsWith(`Your ${rel}`)) ||
      (r.startsWith(`${name} —`) || r.startsWith(`Here's ${name}`)) ||
      (r.includes("what I have") && r.includes(name)) ||
      (r.includes("what I know") && r.includes(name));
    const recentPortraits = ctx.recentResponses.filter(isPortraitLike).length;

    if (recentPortraits >= 2) {
      return PC.composeEntityRepeatedRecall(name, rand);
    }

    const portrait = composeEntityPortrait(name, rand);
    if (portrait) return portrait;
    const response = makeEntityRecallResponse(name, rand);
    if (response) return response;
    return PC.composeEntityUnknown(name, rand);
  }

  // Introduction: "my friend John is..." / "meet Sarah"
  if (isIntroduction(stripped)) {
    return makeSocialIntroResponse(text, rand);
  }

  // Information intake: player providing facts about a known entity via pronoun
  if (RE_PRONOUN_INFO.test(stripped)) {
    const resolvedName = resolvePronouns(stripped);
    if (resolvedName) {
      return makeInfoIntakeResponse(text, resolvedName, rand);
    }
  }

  // Information intake: player providing facts about a named entity directly
  const entityInfoMatch = stripped.match(RE_ENTITY_INFO);
  if (entityInfoMatch) {
    const FUNC_CAPS = new Set(["I", "The", "A", "My", "He", "She", "They", "It", "We", "You",
      "What", "Who", "How", "Why", "When", "Where", "Hey", "Hello", "Hi",
      "Yeah", "Did", "Do", "Is", "Are", "Was", "But", "And", "Or", "So",
      "Not", "Just", "Really", "Also", "This", "That"]);
    const name = entityInfoMatch[1];
    if (!FUNC_CAPS.has(name)) {
      if (!getEntity(name)) {
        processEntities(text);
      }
      if (getEntity(name)) {
        return makeInfoIntakeResponse(text, name, rand);
      }
    }
  }

  // Known-entity catch-all: message mentions a known entity with new info
  {
    const msgWords = stripped.split(/\s+/).filter(Boolean);
    if (msgWords.length >= 4 && !stripped.endsWith("?")) {
      const FUNC_CAPS = new Set(["I", "The", "A", "My", "He", "She", "They", "It", "We", "You",
        "What", "Who", "How", "Why", "When", "Where", "Hey", "Hello", "Hi",
        "Yeah", "Did", "Do", "Is", "Are", "Was", "But", "And", "Or", "So",
        "Not", "Just", "Really", "Also", "This", "That"]);
      const knownName = msgWords.find(w => {
        const clean = w.replace(/[.,!?;:'"]+$/g, "");
        return /^[A-Z][a-z]+$/.test(clean) && !FUNC_CAPS.has(clean) && getEntity(clean) !== null;
      });
      if (knownName) {
        const cleanName = knownName.replace(/[.,!?;:'"]+$/g, "");
        return makeInfoIntakeResponse(text, cleanName, rand);
      }
    }
  }

  // ── Social conventions that VERSE operators cannot handle ──
  // These are not templates — they're social acts that require social responses.
  // "Hello" must get a greeting. "How are you?" must get self-reflection.
  // "You're being rude" must get acknowledgment. VERSE operators transform
  // kernel content, but you can't transform "hello" into a meaningful greeting.

  const wordCount = stripped.split(/\s+/).filter(Boolean).length;

  // Greeting: "Hello!" / "Hey" / "Hi there"
  if (RE_GREETING.test(stripped) && wordCount <= 4) {
    return PC.composeGreeting(stripped, rand);
  }

  // Aeterna-directed question: "How are you?" / "How are you feeling?"
  if (RE_HOW_ARE_YOU.test(stripped) && wordCount <= 6) {
    return PC.composeHowAreYou(stripped, rand);
  }

  // Aeterna self-questions: "What do you think about me?" / "Are you real?"
  if (RE_AETERNA_QUESTION.test(stripped)) {
    if (/^who are you\b/i.test(stripped)) return PC.composeIdentity(stripped, rand);
    if (/^what are you\b/i.test(stripped)) return PC.composeIdentity(stripped, rand);
    if (/^why do you exist\b/i.test(stripped)) return PC.composeExistential(stripped, rand);
    if (/\bhave feelings\b/i.test(stripped)) return PC.composeOpinion(stripped, rand);
    if (/\b(?:real|alive|sentient|conscious)\b/i.test(stripped)) return PC.composeOpinion(stripped, rand);
    if (/\bunderstand\b/i.test(stripped)) return PC.composeUnderstanding(stripped, rand);
    // "What do you think/see/know about me?" → perception
    if (/\b(?:think|feel|know|see) (?:about|of) me\b/i.test(stripped)) return PC.composePerception(stripped, rand);
    // Default: aeterna-directed questions about its own state
    return PC.composeHowAreYou(stripped, rand);
  }

  // Meta-complaint: "Why can't you respond properly?" / "That was off"
  // Must be checked BEFORE compound affect — "wtf why can't you..." is a complaint, not just frustration
  if (RE_META_COMPLAINT.test(stripped)) {
    return PC.composeMetaComplaint(stripped, rand);
  }

  // Meta-commentary: "This could be better" / "More natural"
  if (RE_META_COMMENTARY.test(stripped)) {
    return PC.composeMetaCommentary(stripped, rand);
  }

  // Directed frustration: "You're being rude" / "Fuck you"
  if (RE_DIRECTED_FRUSTRATION.test(stripped)) {
    return PC.composeDirectedFrustration(stripped, rand);
  }

  // Compound affect phrases: "FUCKING WHAT" / "what the fuck" / "fuck yeah"
  // Multi-word phrases whose affect the CharCNN may misread.
  const compound = detectCompoundAffect(stripped);
  if (compound) {
    if ("compose" in compound) {
      if (compound.compose === "amusedExpletive") return PC.composeAmusedExpletive(stripped, rand);
      return PC.composeGreeting(stripped, rand);
    }
    const holdingRecents = new Set(ctx.recentResponses.map(r => r.toLowerCase()));
    return PC.composeHolding(compound.holding, stripped, rand, holdingRecents);
  }

  // ── Conversational acts: social conventions the player performs ──
  const act = detectConversationalAct(stripped);
  if (act === "permission") return PC.composePermission(stripped, rand);
  if (act === "self_diminish") return PC.composeSelfDiminish(stripped, rand);
  if (act === "closure") return PC.composeClosure(stripped, rand);
  if (act === "testing") return PC.composeTesting(stripped, rand);
  if (act === "reassurance") return PC.composeReassurance(stripped, rand);
  if (act === "story_opening") return PC.composeStoryOpening(stripped, rand);
  if (act === "bid") return PC.composeBid(stripped, rand);

  // ── Skeptical / bare challenge: short pushback on previous response ──
  if (RE_SKEPTICAL.test(stripped)) return PC.composeSkeptical(stripped, rand);
  if (RE_BARE_CHALLENGE.test(stripped)) return PC.composeBareChallenge(stripped, rand);

  // ── Emotional distress: SAFETY-CRITICAL — check before disengagement ──
  // "I can't do this anymore" / "I want to disappear" / "nobody would care"
  // Must be caught before dismissive disengage ("I'm done") or denial ("I don't care")
  if (RE_EMOTIONAL_DISTRESS.test(stripped)) return PC.composeEmotionalDistress(stripped, rand);

  // ── Possessive correction: "MY baby, not yours!" ──
  if (RE_POSSESSIVE_CORRECTION_CI.test(stripped) || RE_POSSESSIVE_CAPS.test(stripped)) {
    return PC.composePossessiveCorrection(stripped, rand);
  }

  // ── Dismissive disengagement: "Whatever then" / "I'm done" ──
  if (RE_DISMISSIVE_DISENGAGE.test(stripped)) {
    return PC.composeDismissiveDisengage(stripped, rand);
  }

  // ── Emotional complexity: patterns VERSE would mangle into word-games ──
  // These must be checked before VERSE gets them.

  // Denial/deflection: "I'm fine. I'm fine." / "I don't care"
  const denial = detectDenialDeflection(stripped, rand);
  if (denial) return denial;

  // Emotional contradiction: "I'm happy but also terrified"
  const contradiction = detectEmotionalContradiction(stripped, rand);
  if (contradiction) return contradiction;

  // Social pressure: "Nobody understands" / "Everyone says I should"
  const pressure = detectSocialPressure(stripped, rand);
  if (pressure) return pressure;

  // Emotional overflow: "I can't stop thinking about it"
  const overflow = detectEmotionalOverflow(stripped, rand);
  if (overflow) return overflow;

  // Emotional uncertainty: "I don't know what I'm feeling"
  const uncertainty = detectEmotionalUncertainty(stripped, rand);
  if (uncertainty) return uncertainty;

  // Dream sharing: "I had a weird dream"
  const dream = detectDreamSharing(stripped, rand);
  if (dream) return dream;

  // ── Deep patterns: identity, regret, nostalgia, burnout, distress, etc. ──
  // These are patterns VERSE operators would mangle into word-games.
  // Emotional distress is safety-critical and is checked first inside.
  const deep = detectDeepPatterns(stripped, rand);
  if (deep) return deep;

  // ── Philosophical questions: genuine engagement, not word-games ──
  if (RE_PHILOSOPHICAL.test(stripped)) return PC.composePhilosophical(stripped, rand);

  // ── Event sharing: player narrating what happened ──
  if (RE_POSITIVE_EVENT.test(stripped) || RE_POSITIVE_EXCLAMATION.test(stripped)) {
    return buildPositiveEventResponse(extractEvent(stripped), rand);
  }
  if (RE_NEGATIVE_EVENT.test(stripped)) {
    return buildNegativeEventResponse(extractEvent(stripped), rand);
  }
  if (RE_EVENT_SHARING.test(stripped)) {
    return buildNeutralEventResponse(extractEvent(stripped), rand);
  }

  // ── Mundane/state sharing: casual messages that need casual responses ──
  const mundane = detectMundaneSharing(stripped, intensity, rand);
  if (mundane) return mundane;

  // ── Long-Tail Safety Net ──────────────────────────────────────────────
  // Everything below here is the LAST line of defense before VERSE operators.
  // These catch messages that would produce hollow or broken VERSE output.

  // Slang / reaction words: social signals, not content words.
  // VERSE would nominalize "bruh" into "The bruh is real." — absurd.
  if (RE_SLANG_REACTION.test(stripped)) {
    return PC.composeSlangReaction(stripped, rand);
  }

  // Deflection gates: "it's complicated" / "I don't know where to start"
  // These are emotional GATES. The player wants to talk but needs invitation.
  if (RE_DEFLECTION_GATE.test(stripped)) {
    return PC.composeDeflectionGate(stripped, rand);
  }

  // Non-aeterna engagement questions that need genuine engagement
  if (RE_PLAYFUL_QUESTION.test(stripped)) {
    return PC.composePlayfulQuestion(stripped, rand);
  }
  if (RE_REQUEST_CONTENT.test(stripped)) {
    return PC.composeRequestForContent(stripped, rand);
  }
  if (RE_HYPOTHETICAL_SELF.test(stripped)) {
    return PC.composeHypotheticalAboutSelf(stripped, rand);
  }

  // ── Everything else flows through VERSE ──
  // Vulnerable multi-clause, complex thoughts, and casual sharing all have
  // extractable kernels. With improved kernel scoring (concrete nouns over
  // action verbs, idiomatic intensifier detection), narrative-aware planner
  // (ECHO_TOGETHER + INTERROGATE for casual register), and structural hollow
  // guardrails, VERSE handles these natively — no templates needed.
  return null;
}

// ─── Pipeline ────────────────────────────────────────────────────────────────

/**
 * Generate a response from the Aeterna.
 *
 * This is the single call that the conductor makes. Everything else is
 * internal to the VERSE pipeline.
 */
export function generateResponse(ctx: GeneratorContext): string {
  const rand = mulberry32(hashText(ctx.playerText) ^ (Date.now() & 0xffff));
  const affect = ctx.cortex.immediateAffect;
  const personality = ctx.personality;

  // ── 0a. Entity Extraction ──────────────────────────────────────────
  // Track named entities BEFORE routing — even pre-routed messages like
  // "my friend John is cool" need entity memory.
  // BUT: skip extraction for entity-recall questions ("who is X?") —
  // we don't want the question itself to create the entity.
  if (!RE_ENTITY_RECALL.test(ctx.playerText.trim())) {
    processEntities(ctx.playerText);
  }

  // ── 0b. Pre-Router ──────────────────────────────────────────────────
  // Classify the message register BEFORE kernel extraction. Greetings,
  // meta questions, affective symbols, and compound profanity phrases
  // bypass the full VERSE pipeline and get direct, characterful responses
  // instead of being forced through kernel extraction that would mangle them.
  const preRouted = preRoute(ctx, rand);

  // Always record player text for arc tracking — even pre-routed messages
  // contribute to the emotional territory of the conversation.
  pushPlayerText(ctx.playerText);

  if (preRouted !== null) {
    // Still record in discourse so cross-turn threading stays coherent
    pushKernel(ctx.playerText.slice(0, 32), new Float32Array(8), "player");
    pushKernel(preRouted.slice(0, 32), new Float32Array(8), "aeterna");
    return preRouted;
  }

  // ── 1. Kernel Extraction ──────────────────────────────────────────────
  // Build classification context from cortex state for kernel type tagging
  const classifyCtx: ClassifyContext = {
    intentType: ctx.cortex.intent?.type,
    topic: ctx.cortex.dialogue?.topic,
    dialogueIntent: ctx.cortex.dialogue?.intent,
    hasContradiction: ctx.cortex.semantics?.contradiction?.detected,
    contradictionType: ctx.cortex.semantics?.contradiction?.type,
    hasMetaphor: ctx.cortex.semantics?.metaphor?.detected,
    intensity: affect.intensity,
    melancholy: affect.melancholy,
    warmth: affect.warmth,
    tension: affect.tension,
    depth: affect.depth,
    isDisengaging: ctx.cortex.semantics?.isDisengaging,
    affectVector: affect instanceof Float32Array ? affect : undefined,
  };
  const kernels: KernelResult = extractKernel(ctx.playerText, classifyCtx);

  // Kernel upgrade: if primary has very low charge or no usable content words
  // but a secondary kernel exists, promote the secondary.
  if (kernels.secondary && kernels.primary.charge < 0.3 && kernels.secondary.charge > kernels.primary.charge) {
    const orig = kernels.primary;
    kernels.primary = kernels.secondary;
    kernels.secondary = orig;
  }

  // ── Affect Override ──
  // When the cortex detects emotional weight even if the kernel extractor
  // couldn't find a hot word, bypass low-charge and content gates.
  // A 10-word sentence at high affect should NEVER get "Yeah." — the kernel
  // missed, not the player.
  const wordCount = ctx.playerText.split(/\s+/).filter(Boolean).length;
  const isQuestionMsg = ctx.playerText.trim().endsWith("?");
  const affectOverride = affect.intensity > 0.6 || affect.melancholy >= 0.6 ||
    affect.tension >= 0.6 || affect.depth >= 0.7 ||
    (wordCount >= 6 && affect.depth >= 0.5) ||
    (wordCount >= 5 && isQuestionMsg);

  // Quality gate: if kernel has no real semantic content, use a
  // strategy-appropriate holding response. Prevents "Hey — that feeling." output.
  // EXCEPTION: procedural/greeting/farewell kernel types are expected to have
  // no content words — the kernel-type-aware planner handles them with
  // ECHO_TOGETHER + INTERROGATE, not content-dependent operators.
  const hasContent = kernels.anchors.length > 0 && kernels.anchors[0].length > 2;
  const veryShort = wordCount <= 2;
  const holdingRecents = new Set(ctx.recentResponses.map(r => r.toLowerCase()));
  // Skip content/charge gates for any kernel type that has a dedicated plan.
  // The kernel-type-aware planner handles these without needing content words.
  const TYPES_WITH_PLANS = new Set([
    "procedural", "greeting", "farewell", "emotional_distress", "emotional_state",
    "relational", "meta_complaint", "meta_question", "narrative", "disclosure",
    "vulnerability", "factual", "identity", "rumination", "regret", "transition",
    "event", "entity", "mundane", "philosophical", "playful", "protest", "unknown",
  ]);
  const skipContentGate = TYPES_WITH_PLANS.has(kernels.kernelType);
  if ((!hasContent || (veryShort && kernels.primary.charge < 0.2)) && !affectOverride && !skipContentGate) {
    return holdingResponse(ctx.cortex.coRegTarget?.strategy ?? "expansion", rand, holdingRecents);
  }

  // Low-charge presence: between 0.10 and 0.25, the message is thin but not
  // empty. "I'm fine." "Okay." These aren't nothing — they deserve presence,
  // not "Tell me more." The Aeterna commits instead of deflecting.
  // EXCEPTION: procedural/greeting/farewell skip this gate — the planner handles them.
  if (kernels.primary.charge < 0.10 && !affectOverride && !skipContentGate) {
    return holdingResponse(ctx.cortex.coRegTarget?.strategy ?? "expansion", rand, holdingRecents);
  }
  if (kernels.primary.charge < 0.25 && !affectOverride && !skipContentGate) {
    return PC.composeLowChargePresence(ctx.playerText, rand, holdingRecents);
  }

  // ── 2. Discourse Threading ────────────────────────────────────────────
  const echo: DiscourseEcho | null = findEcho(kernels.primary.embedding);

  // Push the player's kernel into the discourse buffer
  pushKernel(kernels.primary.span, kernels.primary.embedding, "player");

  // ── 3. Planning ───────────────────────────────────────────────────────
  const strategy = ctx.cortex.coRegTarget?.strategy ?? "expansion";
  const vagalState = ctx.cortex.vagalState ?? "engaged";
  const isQuestion = ctx.cortex.intent?.type === "question" ||
    ctx.cortex.dialogue?.intent === "asking" ||
    ctx.playerText.trim().endsWith("?");

  // Retrieve conversation arc for cross-turn emotional awareness
  const arc = getConversationArc();

  const planCtx: PlanContext = {
    kernels,
    kernelType: kernels.kernelType,
    strategy,
    vagalState,
    tension: affect.tension,
    warmth: affect.warmth,
    melancholy: affect.melancholy,
    curiosity: affect.curiosity,
    depth: affect.depth,
    playfulness: affect.playfulness,
    intensity: affect.intensity,
    isQuestion,
    hasDiscourseEcho: echo !== null,
    wordCount: ctx.playerText.split(/\s+/).filter(Boolean).length,
    rand,
    playerText: ctx.playerText,
    arc,
    drives: ctx.cortex.drives,
    dominantDrive: ctx.cortex.dominantDrive
      ? { drive: ctx.cortex.dominantDrive.drive, direction: ctx.cortex.dominantDrive.direction, urgency: ctx.cortex.dominantDrive.urgency }
      : null,
    curiosityDrive: ctx.cortex.curiosity
      ? { overallCuriosity: ctx.cortex.curiosity.overallCuriosity, wantsToAsk: ctx.cortex.curiosity.wantsToAsk, questionDirection: ctx.cortex.curiosity.questionDirection }
      : undefined,
    narrativePhase: ctx.cortex.narrative?.phase,
    dreamResidue: ctx.cortex.dreamResidue,
    semantics: ctx.cortex.semantics
      ? {
          hasMetaphor: ctx.cortex.semantics.metaphor?.detected,
          metaphorCategory: ctx.cortex.semantics.metaphor?.category,
          hasContradiction: ctx.cortex.semantics.contradiction?.detected,
          contradictionType: ctx.cortex.semantics.contradiction?.type,
          contradictionConfidence: ctx.cortex.semantics.contradiction?.confidence,
          isRepetition: ctx.cortex.semantics.isRepetition,
          isDisengaging: ctx.cortex.semantics.isDisengaging,
        }
      : undefined,
    playerBeliefs: ctx.cortex.playerBeliefs
      ? {
          treatsAsSentient: ctx.cortex.playerBeliefs.treatsAsSentient,
          expectsMemory: ctx.cortex.playerBeliefs.expectsMemory,
          sharesVulnerably: ctx.cortex.playerBeliefs.sharesVulnerably,
          testsBoundaries: ctx.cortex.playerBeliefs.testsBoundaries,
          relationalDepth: ctx.cortex.playerBeliefs.relationalDepth,
        }
      : undefined,
    contagion: ctx.cortex.contagion
      ? {
          resonance: ctx.cortex.contagion.resonance,
          empathyDepth: ctx.cortex.contagion.empathyDepth,
          overloaded: ctx.cortex.contagion.overloaded,
          dominantMood: ctx.cortex.contagion.dominantMood,
        }
      : undefined,
    coRegTarget: ctx.cortex.coRegTarget
      ? {
          strategy: ctx.cortex.coRegTarget.strategy,
          urgency: ctx.cortex.coRegTarget.urgency,
        }
      : undefined,
    prediction: ctx.cortex.prediction
      ? {
          overallSurprise: ctx.cortex.prediction.overallSurprise,
          isAnomaly: ctx.cortex.prediction.isAnomaly,
          dominantLevel: ctx.cortex.prediction.dominantLevel,
        }
      : undefined,
    implicitReward: ctx.cortex.implicitReward,
    dialogue: ctx.cortex.dialogue
      ? {
          topic: ctx.cortex.dialogue.topic,
          intent: ctx.cortex.dialogue.intent,
          referent: ctx.cortex.dialogue.referent,
          topicPersistence: ctx.cortex.dialogue.topicPersistence,
          windingDown: ctx.cortex.dialogue.windingDown,
          engagement: ctx.cortex.dialogue.engagement,
          negated: ctx.cortex.dialogue.negated,
          topicReturn: ctx.cortex.dialogue.topicReturn,
          contextSwitch: ctx.cortex.dialogue.contextSwitch,
          repairDetected: ctx.cortex.dialogue.repairDetected,
        }
      : undefined,
    introspection: ctx.cortex.introspection
      ? {
          emotionalTemperature: ctx.cortex.introspection.emotionalTemperature,
          dominantMode: ctx.cortex.introspection.dominantMode,
          selfAwareness: ctx.cortex.introspection.selfAwareness,
        }
      : undefined,
    coherence: ctx.cortex.coherence,
    sceneDrift: ctx.cortex.sceneDriftValue,
    attention: ctx.cortex.attention
      ? {
          isFocused: ctx.cortex.attention.isFocused,
          entropy: ctx.cortex.attention.entropy,
        }
      : undefined,
    attentionHighlights: ctx.cortex.attentionHighlights?.length > 0
      ? ctx.cortex.attentionHighlights
      : undefined,
    memoRecalls: ctx.cortex.memoRecalls?.length > 0
      ? ctx.cortex.memoRecalls.map(r => ({ summary: r.entry.summary, layer: r.layer }))
      : undefined,
  };

  const plan = planResponse(planCtx);

  // Direct response bypasses the operator pipeline entirely.
  // This breaks structural predictability by returning whole sentences
  // that don't follow the segment-assembly cadence.
  if (plan.directResponse) {
    pushKernel(kernels.primary.span, kernels.primary.embedding, "aeterna");
    return plan.directResponse;
  }

  // ── 4. Transformation ─────────────────────────────────────────────────
  const relationalSubject = detectRelationalContext(ctx.playerText);
  const properNouns = extractProperNounsFromText(ctx.playerText);

  const transformCtx: TransformContext = {
    playerText: ctx.playerText,
    strategy,
    vagalState,
    tension: affect.tension,
    warmth: affect.warmth,
    melancholy: affect.melancholy,
    curiosity: affect.curiosity,
    depth: affect.depth,
    playfulness: affect.playfulness,
    patience: personality.patience,
    wit: personality.wit,
    discourseEcho: echo ? { kernel: echo.entry.kernel, turnsAgo: echo.turnsAgo } : null,
    relationalSubject,
    arc,
    inputIsQuestion: isQuestion,
    rand,
    properNouns,
    attentionHighlights: ctx.cortex.attentionHighlights?.length > 0
      ? ctx.cortex.attentionHighlights
      : undefined,
    memoRecalls: ctx.cortex.memoRecalls?.length > 0
      ? ctx.cortex.memoRecalls.map(r => ({ summary: r.entry.summary, layer: r.layer }))
      : undefined,
    prediction: ctx.cortex.prediction ? {
      overallSurprise: ctx.cortex.prediction.overallSurprise,
      isAnomaly: ctx.cortex.prediction.isAnomaly,
      dominantLevel: ctx.cortex.prediction.dominantLevel,
    } : undefined,
  };

  const segments: SynthesisSegment[] = [];
  const seenTexts = new Set<string>();
  const seenCoreWords = new Set<string>();
  for (const step of plan.steps) {
    const operator = OPERATORS[step.operator];
    if (!operator) continue;

    // Feed chaining context to compositional operators (CHAIN, DEEPEN, SURPRISE)
    // so they can connect to the previous segment's output.
    if (segments.length > 0) {
      transformCtx.previousSegment = segments[segments.length - 1].text;
      transformCtx.previousOperator = segments[segments.length - 1].operator;
    } else {
      transformCtx.previousSegment = undefined;
      transformCtx.previousOperator = undefined;
    }
    // Feed the FULL accumulated context — not just the last segment.
    // This enables recursive composition: CHAIN can reference any prior segment,
    // DEEPEN can build on a CHAIN's output, SURPRISE can pivot off a DEEPEN.
    transformCtx.accumulatedSegments = segments.length > 0
      ? segments.map(s => ({ text: s.text, operator: s.operator }))
      : undefined;

    let text = operator(step.kernel, transformCtx);

    // ── Structural hollow guardrail ──────────────────────────────────
    // Detect article-noun fragments ("The knocking."), dangling copulas
    // ("The life is."), and bare noun echoes ("That scratch.") that carry
    // no communicative value. When detected, retry with INTERROGATE
    // (engagement) or ECHO_TOGETHER (presence) as fallback.
    if (text && isStructurallyHollow(text)) {
      // Try INTERROGATE first — engagement > presence for hollow recovery
      const fallbackOps: OperatorType[] = ["INTERROGATE", "ECHO_TOGETHER"];
      for (const fbOp of fallbackOps) {
        const fb = OPERATORS[fbOp];
        if (!fb) continue;
        const fbText = fb(step.kernel, transformCtx);
        if (fbText && fbText.length > 0 && !isStructurallyHollow(fbText)) {
          text = fbText;
          step.operator = fbOp;
          break;
        }
      }
    }

    if (text && text.length > 0) {
      // Deduplicate: don't repeat the same phrase
      const normalized = text.toLowerCase().trim();
      if (seenTexts.has(normalized)) continue;

      // Content-word dedup: extract the primary content word from this segment.
      // Skip if a previous segment already featured this word prominently.
      // Prevents "The thinking. Thinking." and "The missing in that. Missing." redundancy.
      const coreMatch = normalized.replace(/[.…?!,]+/g, "").match(/^(?:the |that |a |an |your |my |this |yeah\.?\s*)?(\w+)/);
      if (coreMatch) {
        const coreWord = coreMatch[1];
        if (!DEDUP_SKIP.has(coreWord)) {
          // Only skip SHORT segments that repeat a core word — don't kill rich phrases
          const segWordCount = normalized.split(/\s+/).length;
          if (segWordCount <= 4 && seenCoreWords.has(coreWord)) continue;
          seenCoreWords.add(coreWord);
        }
      }

      seenTexts.add(normalized);
      segments.push({ text, operator: step.operator });
    }
  }

  // ── 4b. Thin-phrase guard ───────────────────────────────────────────
  // "I hear that." / "I'm here." as the ENTIRE response is deflection.
  // These are fine as one segment in a larger response, but not alone.
  // If that's all we got, force an ECHO of the primary kernel alongside it.
  const THIN_PHRASES = new Set([
    "i hear that.", "i see you.", "say more about that.",
    "tell me more.", "i'm here.", "i know.", "with you.",
    "go on.", "i'm listening.", "i hear you.", "i'm right here.",
  ]);
  if (segments.length === 1 && THIN_PHRASES.has(segments[0].text.toLowerCase().trim())) {
    const echoSeg = OPERATORS.ECHO(kernels.primary, transformCtx);
    if (echoSeg && echoSeg.length > 0) {
      segments.push({ text: echoSeg, operator: "ECHO" });
    }
  }

  // ── 4c. Terminal segment ban ──────────────────────────────────────────
  // "Say more about that." and "Tell me more." cannot be the LAST word.
  // They bounce the player when the Aeterna should commit and land.
  // Only applies when there are multiple segments (it's fine as a solo presence).
  const TERMINAL_BANNED = new Set([
    "say more about that.", "tell me more.", "go on.",
    "what do you mean?", "say more.",
  ]);
  if (segments.length > 1) {
    const lastText = segments[segments.length - 1].text.toLowerCase().trim();
    if (TERMINAL_BANNED.has(lastText)) {
      segments.pop();
    }
  }

  // ── 5. Synthesis ──────────────────────────────────────────────────────
  const prosody: ProsodyParams = {
    patience: personality.patience,
    warmth: personality.warmth ?? affect.warmth,
    wit: personality.wit,
    roomAtmosphere: ctx.cortex.roomNarrative?.atmosphere,
    roomAlignment: ctx.cortex.roomAlignment?.alignment,
  };

  const response = synthesize({
    segments,
    prosody,
    targetWords: plan.targetWords,
    recentResponses: ctx.recentResponses,
    rand,
    playerText: ctx.playerText,
  });

  // ── 6. Discourse Record ───────────────────────────────────────────────
  // Push the Aeterna's primary kernel into the discourse buffer so EVOLVE
  // can track the full conversational arc, not just the player's side.
  if (kernels.primary.span) {
    pushKernel(kernels.primary.span, kernels.primary.embedding, "aeterna");
  }

  return response;
}
